// 继承自ShipMapCanvas.js的业务逻辑
// 该文件将包含原有ShipMap_canvas.vue中的数据和方法

import '@/utils/chinaProvider'; // 导入天地图插件
import 'leaflet-polylinedecorator'; // 导入箭头装饰器插件
import '@/assets/libs/leaflet.canvasmarker'; // 导入CanvasMarker库
import L from 'leaflet'; // 导入Leaflet

// 导入离线地图配置
import { 
  MAP_MODE, 
  getCurrentMapMode, 
  getOfflineTileUrlTemplate,
  OFFLINE_LAYER_CONFIGS 
} from '@/config/mapConfig';

// 导入拆分后的工具模块
import { loadMockLayerData } from '@/utils/mapMockData';
import {
  initLayers, 
  createShipTracksLayer, 
  createWarningAreasLayer, 
  createShipCanvasLayer, 
  updateShipMarkers, 
  createShipIcon, 
  createRadarIcon,
  createRadarTargetLayer,
  updateRadarTargetMarkers,
  updateLayerVisibility
} from '@/utils/mapLayerRenderer';
import {
  convertToDMS, 
  handleMapTypeChange,
  searchShip
} from '@/utils/mapControls';
import { getAreasByBridgeId } from '@/api/area';
import { getBridgeById } from '@/api/bridge';
import { getAllVesselTypes, getVesselByMmsi } from '@/api/vessel';
import mapUtils from '@/utils/mapUtils';
import BridgeEventBus, { BRIDGE_EVENTS } from '@/utils/BridgeEventBus';
import appConfig from '@/config/app-config';
import { createMapLabelManager } from '@/utils/mapLabelManager';

// 额外单独导入这些函数，以便在方法中使用
import { formatCog as formatCogUtil, formatSog as formatSogUtil } from '@/utils/mapControls';

// 导入AIS数据接收器
import { aisDataReceiver, aisEvents, sanitizeVesselName } from '@/utils/AisDataReceiver';
// 导入雷达数据接收器
import { radarDataReceiver, radarEvents } from '@/utils/RadarDataReceiver';

export default {
  zoomIn() {
    if (this.$refs.baseMap) {
      this.$refs.baseMap.zoomIn();
    }
  },
  zoomOut() {
    if (this.$refs.baseMap) {
      this.$refs.baseMap.zoomOut();
    }
  },
  toggleFullscreen() {
    if (this.$refs.baseMap) {
      this.$refs.baseMap.toggleFullscreen();
    }
  },
  onFullscreenChange(isFullscreen) {
    // 通知父组件全屏状态改变
    this.$emit('fullscreen-change', isFullscreen);
    console.log('全屏状态变更:', isFullscreen ? '已进入全屏' : '已退出全屏');
  },
  closeTrackViewer() {
    this.showTrackViewer = false;
  },
  closeShipDialog() {
    this.closeShipPanel();
  },
  onTrackLoaded(trackData) {
    // 处理加载的航迹数据
    console.log('航迹数据加载完成:', trackData);
  },
  data() {
    // 从URL获取地图类型
    const urlType = this.$route && this.$route.query.type;
    const validTypes = ['normal', 'satellite', 'terrain'];
    const initialMapType = validTypes.includes(urlType) ? urlType : 'satellite';
    
    return {
      // 地图配置
      map: null,
      zoom: 12,
      center: [31.231706, 121.472644], // 默认上海位置
      mapOptions: {
        zoomControl: false,
        attributionControl: false
      },
      
      // 添加地图就绪状态标志
      mapReady: false,
      
      // 图层状态
      layerStatus: {
        // 基础底图
        tianditu: false,
        dixingtu: false,
        haitu: false,
        osm: false,
        weixingtu: true,
        
        // 船舶图层
        ship: true,
        radarTarget: true,
        leishenTarget: false,
        aisRadar: false,
        radarLine: false,
        shipWarn: true,
        
        // 桥梁防撞
        bridge: true,
        bridgeReport: true,
        bridgeChannel: true,
        bridgeNavigation: true,
        bridgeWarn: true,
        bridgeForbid: true,
        
        // 设备管理
        cctv: false,
        box: false,
        laser: false,
        radar: false,
        relay: false,
        
        // 其它
        hxjg: false,
        bridgeMast: true,
        shipTracks: true,
        warningAreas: true,
      },
      
      // 图层初始化状态跟踪
      layerInitStatus: {
        canvasInitialized: false,      // Canvas图层是否已初始化
        tracksInitialized: false,      // 航迹图层是否已初始化
        warningAreasInitialized: false, // 警戒区域是否已初始化
        radarLayerInitialized: false,  // 雷达目标图层是否已初始化
      },
      
      // 文本标注防重叠布局管理器
      mapLabelManager: null,
      
      // 图层管理器
      layerManagerVisible: false,

      // 热力图功能面板
      heatmapPanelVisible: false,
      heatmapLayerSnapshot: null,

      // 防重叠开关（临时调试用）
      antiOverlapEnabled: true,
      
      // 搜索相关
      search: '',
      isSearching: false,
      
      // 船舶详情弹窗
      shipDialogVisible: false,
      currentShip: null,
      
      // 船舶详情面板
      shipPanelVisible: false,
      
      // 船舶类型选项
      vesselTypeOptions: [],
      vesselTypeLoaded: false,
      
      // 当前高亮的船舶和高亮标记
      currentHighlightShip: null,
      highlightMarker: null,
      highlightInterval: null,
      
      // 船舶航迹查看
      showTrackViewer: false,
      currentShipMmsi: null,
      trackTimeRange: null,
      trackQueryMode: {
        useLatestRecords: true,
        maxRecords: 10
      },
      // 保存显示航迹前的船舶图层状态
      shipLayerStateBeforeTrack: null,
      
      // 多桥梁选择相关
      selectedBridges: [], // 选中的桥梁对象列表
      selectedBridgeIds: [], // 选中的桥梁ID列表
      
      // 预警信息相关
      noticeContentVisible: false,
      activeNoticeTab: 'warning',
      vhfEnabled: true,
      soundEnabled: true,
      noticeCount: 5,
      warningCount: 3,
      navigationCount: 2,
      noticeFilters: {
        deviation: true,
        height: true,
        speed: true,
        stay: true
      },
      
      // 模拟预警通知数据
      warningNotices: [
        {
          id: 1,
          mmsi: '413456789',
          shipName: 'COSTA VENUS',
          message: '偏航预警',
          level: '危险',
          time: '2023-06-05 14:30:22',
          type: 'deviation'
        },
        {
          id: 2,
          mmsi: '477123456',
          shipName: '长江明珠',
          message: '超高预警',
          level: '警告',
          time: '2023-06-05 14:15:06',
          type: 'height'
        },
        {
          id: 3,
          mmsi: '413998877',
          shipName: 'MSC GULSUN',
          message: '超速预警',
          level: '警告',
          time: '2023-06-05 13:55:43',
          type: 'speed'
        }
      ],
      navigationNotices: [
        {
          id: 4,
          mmsi: '413123456',
          shipName: 'MAERSK HONAM',
          message: '进入通航区',
          time: '2023-06-05 14:05:18',
          type: 'navigation'
        },
        {
          id: 5,
          mmsi: '413654321',
          shipName: 'EVER GOLDEN',
          message: '离开通航区',
          time: '2023-06-05 13:45:30',
          type: 'navigation'
        }
      ],
      
      // Canvas图层相关
      canvasLayer: null,
      shipIcon: null,
      shipData: [],
      ships: [],
      shipMarkers: [],
      shipTracks: [],
      warningAreas: [],
      polygonWarningAreas: [], // 非点标注的区域数据，用于mapLayerRenderer
      // 区域数据备份，用于确保数据在切换可见性时不会丢失
      areaDataBackup: [],
      
      // 当前地图类型
      currentMapType: initialMapType,
      // 天地图key
      tiandituKey: '038937b9b91fd28203af58e3dd9b1396',
      // 图层配置
      layers: [],
      // 鼠标位置
      mousePosition: {
        lat: 0,
        lng: 0
      },
      // 船舶航迹线图层组
      shipTracksLayerGroup: null,
      // 警戒区域图层组
      warningAreasLayerGroup: null,
      // 🗺️ 离线海图叠加图层
      offlineSeaLayer: null,
      
      // 添加地图初始化标志
      mapInitialized: false,
      
      // AIS数据接收器
      aisDataReceiver: null,
      
      // 雷达数据接收器
      radarDataReceiver: null,
      
      // 雷达目标数据
      radarTargets: [],
      
      // 雷达图层相关
      radarIcon: null,
      radarTargetLayer: null,
      radarTargetMarkers: [],
      radarLayerInitialized: false,
      radarDialogVisible: false,
      currentRadarTarget: null,
    };
  },
  computed: {
    filteredWarningNotices() {
      return this.warningNotices.filter(notice => {
        if (notice.type === 'deviation' && !this.noticeFilters.deviation) return false;
        if (notice.type === 'height' && !this.noticeFilters.height) return false;
        if (notice.type === 'speed' && !this.noticeFilters.speed) return false;
        if (notice.type === 'stay' && !this.noticeFilters.stay) return false;
        return true;
      });
    },
    // 格式化经度显示
    formatLongitude() {
      return convertToDMS ? convertToDMS(this.mousePosition.lng, 'lng') : this.mousePosition.lng.toFixed(6);
    },
    // 格式化纬度显示
    formatLatitude() {
      return convertToDMS ? convertToDMS(this.mousePosition.lat, 'lat') : this.mousePosition.lat.toFixed(6);
    },
    
    // 从区域数据中过滤出点标注
    pointMarkers() {
      // 热力图纯净模式：仅显示底图与轨迹，隐藏点标注（如"上游右激光"等设备点位）
      if (this.heatmapPanelVisible) {
        return [];
      }
      if (!this.warningAreas || !Array.isArray(this.warningAreas)) {
        return [];
      }
      // console.log('warningAreas',this.warningAreas)
      
      const pointAreas = this.warningAreas.filter(area => area.geometryType === 'POINT');
      console.log('点标注数据过滤结果:', pointAreas.length, '个点标注');
      
      return pointAreas
        .map(area => {
          // 获取点坐标
          const coords = area.coordinates && area.coordinates.length > 0 ? area.coordinates[0] : null;
          if (!coords || coords.length < 2) {
            console.warn('点标注坐标无效:', area.name, coords);
            return null;
          }
          // console.log('area',area)

          const iconName = area.icon || area.iconName;
          // console.log(`点标注 "${area.name}" 类型: ${area.type}, 图标: ${iconName}, 坐标:`, coords);
          
          return {
            id: area.id,
            name: area.name,
            label: area.name,
            position: [Number(coords[0]), Number(coords[1])], // 修改为position格式
            description: `类型: ${area.type}`,
            type: area.type,
            areaType: area.type,
            color: area.color || '#ff4444',
            iconName: iconName, // 根据区域类型获取图标
            visible: true
          };
        })
        .filter(Boolean); // 过滤掉null值
    }
  },
  watch: {
    heatmapPanelVisible(visible) {
      if (visible) {
        this.enterHeatmapCleanMode()
      } else {
        this.exitHeatmapCleanMode()
      }
    }
  },
  created() {
    console.log('【ShipMapView】ShipMapView组件已创建');
    
    // 加载船舶类型数据
    this.loadVesselTypes();
    
    // 初始化图层配置
    this.initLayers();
    
    // 确保warningAreas是数组
    this.warningAreas = this.warningAreas || [];
    this.polygonWarningAreas = this.polygonWarningAreas || [];
    this.areaDataBackup = this.areaDataBackup || [];
    
    // 设置图层默认可见性
    this.layerStatus.shipTracks = true;
    this.layerStatus.warningAreas = true;
    
    // 加载模拟图层数据
    // this.loadMockLayerData();
    
    // 从localStorage获取保存的桥梁信息并设置初始地图中心点
    this.initMapCenterFromBridge();
    
    // 添加桥梁变更事件监听
    this.$root.$on('bridge-changed', this.handleBridgeChange);
    // 添加多桥梁选择变更事件监听
    console.log('【ShipMapView】注册bridges-selection-changed事件监听器');
    this.$root.$on('bridges-selection-changed', this.handleBridgesSelectionChange);
    // 添加船舶更新事件监听，更新船舶图层。并在本组件销毁时移除
    BridgeEventBus.$on(BRIDGE_EVENTS.SHIP_UPDATED, this.updateShipMarkers);
    
    // 监听桥梁选择变更事件，重新过滤船舶显示
    BridgeEventBus.$on(BRIDGE_EVENTS.BRIDGE_SELECTED, this.handleBridgeChanged);

    // 全局引用，方便调试
    if (window) {
      window.shipMapView = this;
    }
  },
  mounted() {
    console.log('ShipMapView组件已挂载');
    
    // 不再在mounted中初始化AIS数据接收器
    // 而是在地图就绪后初始化
    
    // 如果地图已经初始化，执行相应的初始化
    if (this.map && !this.mapInitialized) {
      this.mapInitialized = true;
    }
  },
  beforeDestroy() {
    console.log('ShipMapView组件销毁前清理资源');
    
    // 通知数据接收器地图不再就绪
    if (this.aisDataReceiver && typeof this.aisDataReceiver.setMapReady === 'function') {
      this.aisDataReceiver.setMapReady(false);
    }
    
    if (this.radarDataReceiver && typeof this.radarDataReceiver.setMapReady === 'function') {
      this.radarDataReceiver.setMapReady(false);
    }
    
    // 停止所有计时器
    if (this._shipUpdateTimeout) {
      clearTimeout(this._shipUpdateTimeout);
      this._shipUpdateTimeout = null;
    }
    
    // 清除高亮效果
    this.clearHighlight();
    
    // 移除事件监听
    this.removeAisEventListeners();
    this.removeRadarEventListeners();
    this.removeShipEventListeners(); // 移除船舶事件监听
    
    // 断开数据连接
    if (this.aisDataReceiver) {
      this.aisDataReceiver.disconnect().catch(err => {
        console.warn('断开AIS数据连接失败:', err);
      });
    }
    
    if (this.radarDataReceiver) {
      this.radarDataReceiver.disconnect().catch(err => {
        console.warn('断开雷达数据连接失败:', err);
      });
    }
    
    // 清理所有图层
    this.cleanupLayers();
    
    // 移除地图事件
    if (this.map) {
      this.map.off('mousemove', this.onMouseMove);
      this.map = null;
    }
    
    // 移除事件监听
    this.$root.$off('bridge-changed', this.handleBridgeChange);
    this.$root.$off('bridges-selection-changed', this.handleBridgesSelectionChange);
    BridgeEventBus.$off(BRIDGE_EVENTS.SHIP_UPDATED, this.updateShipMarkers);
    BridgeEventBus.$off(BRIDGE_EVENTS.BRIDGE_SELECTED, this.handleBridgeChanged);
    
    // 移除全局引用
    if (window && window.shipMapView === this) {
      window.shipMapView = null;
    }
    
    // 重置状态标志
    this.layerInitStatus.canvasInitialized = false;
    this.layerInitStatus.tracksInitialized = false;
    this.layerInitStatus.warningAreasInitialized = false;
    this.mapInitialized = false;
    this.mapReady = false;
  },
  methods: {
    enterHeatmapCleanMode() {
      if (this.heatmapLayerSnapshot || !this.map) return
      this.heatmapLayerSnapshot = { ...this.layerStatus }
      const basemapKeys = ['tianditu', 'dixingtu', 'haitu', 'osm', 'weixingtu']
      Object.keys(this.layerStatus).forEach((k) => { this.layerStatus[k] = false })
      basemapKeys.forEach((k) => { this.layerStatus[k] = this.heatmapLayerSnapshot[k] })
      if (this.toggleShipVisibility) this.toggleShipVisibility(false)
      if (this.toggleShipTracksVisibility) this.toggleShipTracksVisibility(false)
      if (this.toggleRadarTargetVisibility) this.toggleRadarTargetVisibility(false)
      if (this.warningAreasLayerGroup && this.map) {
        try {
          if (this.map.hasLayer(this.warningAreasLayerGroup)) this.map.removeLayer(this.warningAreasLayerGroup)
        } catch (e) { console.warn('隐藏警戒区域图层失败:', e) }
        this.warningAreasLayerGroup = null
      }
      this.updateLayerVisibility()
    },
    exitHeatmapCleanMode() {
      if (!this.heatmapLayerSnapshot) return
      const snap = this.heatmapLayerSnapshot
      this.heatmapLayerSnapshot = null
      Object.keys(this.layerStatus).forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(snap, k)) this.layerStatus[k] = snap[k]
      })
      if (this.toggleShipVisibility) this.toggleShipVisibility(this.layerStatus.ship)
      if (this.toggleShipTracksVisibility) this.toggleShipTracksVisibility(this.layerStatus.shipTracks)
      if (this.toggleRadarTargetVisibility) this.toggleRadarTargetVisibility(this.layerStatus.radarTarget)
      if (this.layerStatus.warningAreas && this.toggleWarningAreaDisplay) this.toggleWarningAreaDisplay(false)
      this.updateLayerVisibility()
    },
    // 判断图层是否为警戒区域相关图层的辅助函数
    isWarningAreaLayer(layer) {
      return (
        // 原有的检测条件
        layer.isWarningAreaGroup || 
        layer.name === 'warningAreasLayerGroup' ||
        (layer._leaflet_id && layer._leaflet_id.toString().includes('WarningArea')) ||
        (layer.options && layer.options.isWarningArea) ||
        (layer.feature && layer.feature.properties && layer.feature.properties.isWarningArea) ||
        // 新增：检测已明确标记的LayerGroup类型的警戒区域图层
        // 注意：普通 LayerGroup（如航迹、雷达等）不算警戒区域，必须带 isWarningAreaGroup 标记或固定名称
        (layer instanceof L.LayerGroup && layer.getLayers && layer.getLayers().length > 0 &&
         (layer.isWarningAreaGroup === true || layer.name === 'warningAreasLayerGroup')) ||
        // 检测具有区域标签的几何对象（更安全的检测方式）
        ((layer instanceof L.Polygon || layer instanceof L.Circle || layer instanceof L.Polyline) && 
         layer.getTooltip && layer.getTooltip() && layer.getTooltip().getContent() &&
         // 确保tooltip内容看起来像区域名称（包含中文字符或常见区域关键词）
         (/[\u4e00-\u9fa5]/.test(layer.getTooltip().getContent()) || 
          /区|桥|航道|警戒|禁止/i.test(layer.getTooltip().getContent())))
      );
    },

    // 强制移除所有警戒区域图层
    forceRemoveAllWarningAreaLayers() {
      if (!this.map) {
        console.warn('地图未初始化，无法清除图层');
        return;
      }
      
      // 统计地图上的所有图层
      let allLayerDetails = [];
      
      this.map.eachLayer(layer => {
        // 记录所有图层的详细信息用于调试
        allLayerDetails.push({
          id: layer._leaflet_id,
          name: layer.name,
          constructor: layer.constructor.name,
          isWarningAreaGroup: layer.isWarningAreaGroup,
          hasTooltip: !!layer.getTooltip,
          tooltipContent: layer.getTooltip ? layer.getTooltip()?.getContent() : null
        });
      });
      
      // 1. 清除当前的warningAreasLayerGroup
      if (this.warningAreasLayerGroup) {
        try {
          if (this.map.hasLayer(this.warningAreasLayerGroup)) {
            this.map.removeLayer(this.warningAreasLayerGroup);
          }
          
          // 清除图层内容
          if (this.warningAreasLayerGroup.clearLayers) {
            this.warningAreasLayerGroup.clearLayers();
          }
        } catch (e) {
          console.warn('移除主要警戒区域图层失败:', e);
        }
        this.warningAreasLayerGroup = null;
      }
      
      // 2. 遍历地图上所有图层，移除可能残留的警戒区域图层
      const allLayers = [];
      this.map.eachLayer(layer => {
        // 使用辅助函数检测警戒区域图层
        if (this.isWarningAreaLayer(layer)) {
          allLayers.push(layer);
        }
      });
      
      allLayers.forEach(layer => {
        try {
          this.map.removeLayer(layer);
          if (layer.clearLayers) {
            layer.clearLayers();
          }
        } catch (e) {
          console.warn('移除残留图层失败:', e);
        }
      });
      
      // 3. 注意：不再清空 this.warningAreas / this.polygonWarningAreas 数据。
      //    本方法只负责移除地图上的图层，数据由 loadBridgeAreas / loadMultipleBridgeAreas
      //    流程统一管理，避免数据加载完成后又被本方法清空导致区域标注丢失。
    },

    // 地图相关方法
    initLayers() {
      // 使用工具函数初始化图层
      if (initLayers) {
        this.layers = initLayers(this.currentMapType, this.tiandituKey, true);
      } else {
        console.log('初始化地图图层');
      }
    },
    
    zoomUpdated(zoom) {
      this.zoom = zoom;
    },
    
    centerUpdated(center) {
      // 确保center总是以数组形式存储
      if (center && Array.isArray(center)) {
        this.center = center;
      } else if (center && typeof center === 'object' && 'lat' in center && 'lng' in center) {
        // 如果接收到的是一个包含lat和lng的对象，则转换为数组
        this.center = [center.lat, center.lng];
      }
    },
    
    onMouseMove(e) {
      if (e && e.latlng) {
        this.mousePosition = {
          lat: e.latlng.lat,
          lng: e.latlng.lng
        };
        this.$emit('mouse-move', e.latlng);
      }
    },
    
    // 图层管理相关方法
    toggleLayerManager() {
      this.layerManagerVisible = !this.layerManagerVisible;
    },

    // 热力图功能面板
    toggleHeatmapPanel() {
      this.heatmapPanelVisible = !this.heatmapPanelVisible;
    },

    // 防重叠开关（临时调试用）
    toggleAntiOverlap() {
      this.antiOverlapEnabled = !this.antiOverlapEnabled;
      if (!this.antiOverlapEnabled) {
        // 关闭：销毁管理器，清除所有位移和引线
        if (this.mapLabelManager) {
          this.mapLabelManager.destroy && this.mapLabelManager.destroy();
          this.mapLabelManager = null;
        }
        // 清除所有 area-label 的 margin
        if (this.map) {
          this.map.getContainer().querySelectorAll('.area-label').forEach(el => {
            el.style.marginLeft = '';
            el.style.marginTop = '';
            el.style.display = '';
          });
          // 清除引线 SVG（在地图 referenceLinePane 下）
          const svg = this.map.getContainer().querySelector('svg[data-label-lines]');
          if (svg) svg.remove();
        }
        console.log('[防重叠] 已关闭');
      } else {
        // 开启：重新创建管理器
        if (this.map && !this.mapLabelManager) {
          this.mapLabelManager = createMapLabelManager(this.map, {
            collisionSelector: '.area-label',
            drawReferenceLine: true
          });
          this.mapLabelManager.layout();
          console.log('[防重叠] 已开启');
        }
      }
    },
    
    switchLayer(event) {
      const layerTitle = event.currentTarget.getAttribute('title');
      console.log('切换图层按钮点击:', layerTitle);
      
      if (layerTitle && Object.prototype.hasOwnProperty.call(this.layerStatus, layerTitle)) {
        // 切换当前图层状态
        this.layerStatus[layerTitle] = !this.layerStatus[layerTitle];
        console.log(`图层状态变更: ${layerTitle}, ${!this.layerStatus[layerTitle]} => ${this.layerStatus[layerTitle]}`);
        
        // 处理特殊的图层类型
        if (layerTitle === 'tianditu' && this.layerStatus[layerTitle]) {
          // 点击天地图时，切换到天地图类型
          this.changeMapType('normal');
          // 同时关闭其他基础底图
          this.layerStatus.dixingtu = false;
          this.layerStatus.weixingtu = false;
        } 
        else if (layerTitle === 'dixingtu' && this.layerStatus[layerTitle]) {
          // 点击地形图时，切换到地形图类型
          this.changeMapType('terrain');
          // 同时关闭其他基础底图
          this.layerStatus.tianditu = false;
          this.layerStatus.weixingtu = false;
        }
        else if (layerTitle === 'weixingtu' && this.layerStatus[layerTitle]) {
          // 点击卫星图时，切换到卫星图类型
          this.changeMapType('satellite');
          // 同时关闭其他基础底图
          this.layerStatus.tianditu = false;
          this.layerStatus.dixingtu = false;
        }
        else if (layerTitle === 'ship') {
          // 处理船舶图层的显隐
          this.toggleShipVisibility(this.layerStatus.ship);
        }
        else if (layerTitle === 'warningAreas') {
          // 使用统一的方法处理警戒区域图层
          console.log('通过图层管理按钮切换警戒区域，新状态:', this.layerStatus.warningAreas);
          // 对于首次切换，强制创建图层
          const isFirstToggle = !this.warningAreasLayerGroup || this.warningAreasLayerGroup.isEmpty;
          this.toggleWarningAreaDisplay(isFirstToggle);
        }
        else if (layerTitle === 'shipTracks') {
          // 处理船舶航迹图层的显隐，直接使用状态变量
          this.toggleShipTracksVisibility(this.layerStatus.shipTracks);
        }
        else if (layerTitle === 'radarTarget') {
          // 处理雷达目标图层的显隐
          this.toggleRadarTargetVisibility(this.layerStatus.radarTarget);
        }
        else if (layerTitle === 'haitu') {
          // 🗺️ 处理海图图层的显隐（叠加离线海图到天地图上）
          this.toggleOfflineSeaLayer(this.layerStatus.haitu);
        }
        
        // 更新其他图层的可见性
        this.updateLayerVisibility();
      }
    },
    
    updateLayerVisibility() {
      // 使用工具函数更新图层可见性
      if (!this.map) {
        console.warn('无法更新图层可见性：地图未初始化');
        return;
      }
      
      // 排除已经通过专用方法处理的图层
      const skipLayers = ['warningAreas', 'shipTracks', 'ship'];
      
      // 使用工具函数处理其他图层
      if (updateLayerVisibility) {
        const layerGroups = {};
        
        // 只处理未特殊处理的图层
        Object.entries(this.layerStatus)
          .filter(([key]) => !skipLayers.includes(key))
          .forEach(([key]) => {
            // 根据命名规则构造图层组名称
            const groupKey = `${key}LayerGroup`;
            if (this[groupKey]) {
              layerGroups[groupKey] = this[groupKey];
            }
          });
        
        // 调用工具函数处理其他图层
        updateLayerVisibility(this.map, layerGroups, this.layerStatus);
      }
    },
    
    // 船舶相关方法
    createShipIcon() {
      // 创建船舶图标
      if (createShipIcon) {
        this.shipIcon = createShipIcon();
      } else {
        console.log('创建船舶图标');
      }
    },
    
    initCanvasLayer() {
      // 如果图层已经创建，不再重复初始化
      if (this.layerInitStatus.canvasInitialized && this.canvasLayer) {
        console.log('Canvas图层已经初始化，无需重复创建');
        return;
      }
      
      // 如果地图未初始化，直接退出
      if (!this.map) {
        console.log('地图未初始化，不创建Canvas图层');
        return;
      }
      
      console.log('开始初始化Canvas图层');
      
      // 创建船舶图标（如果尚未创建）
      if (!this.shipIcon) {
        this.createShipIcon();
      }
      
      // 清理已有的Canvas图层
      this.cleanupCanvasLayer();
      
      // 仅在地图完全就绪时创建图层
      if (createShipCanvasLayer && this.map._container && this.map._panes && this.map._panes.overlayPane) {
        try {
          // 创建新的Canvas图层
          this.canvasLayer = createShipCanvasLayer(
            this.map, 
            this.shipIcon, 
            this.ships,
            this.showShipInfo
          );
          
          // 设置图层完成初始化的标志
          if (this.canvasLayer) {
            console.log('Canvas图层创建成功');
            this.layerInitStatus.canvasInitialized = true;
            
            // 确保Canvas图层在最顶层
            if (this.canvasLayer._container) {
              this.canvasLayer._container.style.zIndex = 300;
            }
            
            // 根据船舶图层状态决定是否添加到地图
            if (this.layerStatus.ship) {
              // 立即更新船舶标记
              if (this.ships && this.ships.length > 0) {
                // console.log('初始化完成后立即更新船舶标记，数量:', this.ships.length);
                this.updateShipMarkers();
              }
              
              // 确保图层添加到地图
              if (!this.map.hasLayer(this.canvasLayer)) {
                this.map.addLayer(this.canvasLayer);
              }
            } else {
              console.log('船舶图层未启用，不添加到地图');
            }
          } else {
            console.warn('Canvas图层创建失败');
          }
        } catch (e) {
          console.error('创建Canvas图层时出错:', e);
        }
      } else {
        console.warn('地图未完全就绪或工具函数不可用，暂不创建Canvas图层');
      }
    },
    
    // 添加清理Canvas图层的专用方法
    cleanupCanvasLayer() {
      if (this.canvasLayer) {
        try {
          console.log('清理现有Canvas图层');
          
          // 移除所有船舶标记
          if (this.shipMarkers && this.shipMarkers.length > 0) {
            try {
              this.canvasLayer.removeLayers(this.shipMarkers);
            } catch (e) {
              console.warn('移除船舶标记失败:', e);
            }
            this.shipMarkers = [];
          }
          
          // 从地图中移除图层
          if (this.map && this.map.hasLayer(this.canvasLayer)) {
            this.map.removeLayer(this.canvasLayer);
          }
          
          // 重置图层引用和状态
          this.canvasLayer = null;
          this.layerInitStatus.canvasInitialized = false;
        } catch (e) {
          console.warn('清理Canvas图层时出错:', e);
        }
      }
    },
    
    toggleShipVisibility(visible) {
      if (!this.canvasLayer || !this.map) {
        console.warn('Canvas图层未初始化，无法切换船舶可见性');
        return;
      }
      
      console.log(`切换船舶图标可见性: ${visible ? '显示' : '隐藏'}, 标记数量:`, this.shipMarkers.length);
      
      try {
        if (visible) {
          // 显示船舶图标
          if (this.shipMarkers.length === 0) {
            // 如果没有标记，重新创建
            this.updateShipMarkers();
          } else {
            // 如果已有标记，添加到图层
            this.canvasLayer.addLayers(this.shipMarkers);
          }
          // 强制重绘Canvas图层
          this.canvasLayer._redraw(true);
          
          // 确保地图包含这个图层
          if (!this.map.hasLayer(this.canvasLayer)) {
            this.map.addLayer(this.canvasLayer);
          }
        } else {
          // 隐藏船舶图标
          if (this.shipMarkers.length > 0) {
            this.canvasLayer.removeLayers(this.shipMarkers);
            // 强制重绘Canvas图层，清除已删除的标记
            this.canvasLayer._redraw(true);
          }
          
          // 确保从地图中移除图层
          if (this.map.hasLayer(this.canvasLayer)) {
            this.map.removeLayer(this.canvasLayer);
          }
        }
      } catch (error) {
        console.error('切换船舶可见性失败:', error);
      }
    },
    
    // 🗺️ 切换离线海图叠加层
    toggleOfflineSeaLayer(visible) {
      if (!this.map) {
        console.warn('地图未初始化，无法切换海图图层');
        return;
      }
      
      // 如果当前是离线地图模式，海图已经作为基础图层存在，不需要额外处理
      const currentMapMode = getCurrentMapMode();
      if (currentMapMode === MAP_MODE.OFFLINE) {
        console.log('当前为离线地图模式，海图已作为基础图层存在');
        return;
      }
      
      console.log(`切换离线海图叠加层: ${visible ? '显示' : '隐藏'}`);
      
      if (visible) {
        // 创建并添加离线海图叠加层
        if (!this.offlineSeaLayer) {
          const seaConfig = OFFLINE_LAYER_CONFIGS.sea;
          const seaUrl = getOfflineTileUrlTemplate('sea');
          
          console.log('海图配置:', seaConfig);
          console.log('海图URL模板:', seaUrl);
          
          this.offlineSeaLayer = L.tileLayer(seaUrl, {
            minZoom: seaConfig.minZoom,
            maxZoom: seaConfig.maxZoom,
            // 海图瓦片自带透明度，不需要额外设置opacity
            attribution: '离线海图',
            zIndex: 250 // 确保在基础底图之上，但在其他叠加层之下
          });
          
          console.log('创建离线海图图层完成');
        }
        
        // 添加到地图
        if (!this.map.hasLayer(this.offlineSeaLayer)) {
          this.offlineSeaLayer.addTo(this.map);
          console.log('离线海图图层已添加到地图');
        }
      } else {
        // 从地图移除离线海图叠加层
        if (this.offlineSeaLayer && this.map.hasLayer(this.offlineSeaLayer)) {
          this.map.removeLayer(this.offlineSeaLayer);
          console.log('离线海图图层已从地图移除');
        }
      }
    },
    
    searchShip() {
      if (!this.search.trim()) return;
      
      this.isSearching = true;
      
      // 第一步：在本地船舶数据中搜索
      if (searchShip) {
        const foundShip = searchShip(this.search, this.ships);
        
        if (foundShip) {
          // 在当前图层找到船舶，直接显示
          if (foundShip.pos) {
            this.center = [foundShip.pos.latitude, foundShip.pos.longitude];
            this.zoom = 14;
            this.showShipInfo(foundShip);
          } else {
            this.$message.warning('找到的船舶没有位置信息');
            this.showShipInfo(foundShip);
          }
          this.isSearching = false;
          return;
        }
        
        // 第二步：如果本地没找到，尝试通过API查询MMSI
        const searchText = this.search.trim();
        console.log(`本地未找到船舶，尝试通过API查询MMSI: ${searchText}`);
        
        // 检查搜索文本是否可能是MMSI（数字格式）
        if (/^\d+$/.test(searchText)) {
          this.searchVesselByMmsiApi(searchText);
        } else {
          // 非MMSI格式，直接创建基本对象
          this.createBasicShipInfo(searchText);
        }
      } else {
        // searchShip函数不可用，使用备用搜索逻辑
        console.log('searchShip函数不可用，使用备用搜索逻辑');
        this.fallbackSearchLogic();
      }
    },
    
    /**
     * 通过API查询船舶MMSI信息
     */
    async searchVesselByMmsiApi(mmsi) {
      try {
        console.log(`正在查询MMSI: ${mmsi}`);
        const response = await getVesselByMmsi(mmsi);        
        if (response.code === 200 && response.data) {
          // API查询成功，构建船舶对象
          const vesselData = response.data;
          const apiShip = {
            userid: vesselData.mmsi,
            shipname: sanitizeVesselName(vesselData.vesselName || vesselData.cnName) || vesselData.mmsi,
            cnName: sanitizeVesselName(vesselData.cnName || vesselData.vesselName) || vesselData.mmsi,
            pos: null, // API数据通常不包含实时位置
            sog: undefined,
            cog: undefined,
            heading: undefined,
            callSign: vesselData.callSign || '-',
            flag: vesselData.flag || '-',
            shipType: vesselData.vesselType,
            contacts: vesselData.contacts || '-',
            contactNumber: vesselData.contactNumber || '-',
            length: vesselData.length,
            width: vesselData.width,
            depth: vesselData.depth,
            load: vesselData.load,
            grossTonnage: vesselData.grossTonnage,
            buildYear: vesselData.buildYear,
            imo: vesselData.imo,
            remark: vesselData.remark || '船舶基本信息'
          };
            
          this.showShipInfo(apiShip);
          // this.$message.success(`已找到船舶信息：${apiShip.cnName || apiShip.shipname}`);
          
          // 🔥 关键优化：更新ships数组中的船舶信息，以便地图上正确显示中文名称
          this.updateShipInShipsArray(mmsi, vesselData);
          
        } else {
          // API查询失败或无数据，创建基本对象
          console.log(`API查询无结果: ${response.message || '未找到数据'}`);
          this.createBasicShipInfo(mmsi);
        }
      } catch (error) {
        console.error('API查询船舶信息失败:', error);
        // API查询出错，创建基本对象
        this.createBasicShipInfo(mmsi);
      } finally {
        this.isSearching = false;
      }
    },
    
    /**
     * 创建基本船舶信息对象
     */
    createBasicShipInfo(searchText) {
      const basicShip = {
        userid: searchText,
        shipname: searchText,
        cnName: `搜索结果: ${searchText}`,
        pos: null,
        sog: undefined,
        cog: undefined,
        heading: undefined,
        callSign: '-',
        flag: '-',
        shipType: undefined,
        contacts: '-',
        contactNumber: '-',
        length: undefined,
        width: undefined,
        depth: undefined,
        load: undefined,
        remark: '可查询航迹记录'
      };
      
      this.showShipInfo(basicShip);
      this.$message.info('已显示船舶信息面板，可查询航迹');
      this.isSearching = false;
    },
    
    /**
     * 备用搜索逻辑（当searchShip函数不可用时使用）
     */
    fallbackSearchLogic() {
      // 模拟搜索本地数据
      setTimeout(async () => {
          const foundShip = this.shipData.find(ship => 
            ship.userid.includes(this.search) || 
            (ship.shipname && ship.shipname.includes(this.search))
          );
          
          if (foundShip) {
          // 找到本地船舶
            this.currentShip = foundShip;
            this.shipDialogVisible = true;
            
            // 如果有位置，将地图中心定位到船舶位置
            if (foundShip.pos) {
              this.map.setView([foundShip.pos.latitude, foundShip.pos.longitude], 15);
              // 添加高亮效果
              this.highlightShip(foundShip);
            }
          this.isSearching = false;
          } else {
          // 本地未找到，尝试API查询（如果是MMSI格式）
          const searchText = this.search.trim();
          if (/^\d+$/.test(searchText)) {
            await this.searchVesselByMmsiApi(searchText);
          } else {
            this.createBasicShipInfoFallback(searchText);
          }
        }
      }, 800);
    },
    
    /**
     * 备用方案创建基本船舶信息
     */
    createBasicShipInfoFallback(searchText) {
      const basicShip = {
        userid: searchText,
        shipname: searchText,
        cnName: `搜索结果: ${searchText}`,
        pos: null,
        sog: undefined,
        cog: undefined,
        heading: undefined,
        callSign: '-',
        flag: '-',
        shipType: undefined,
        contacts: '-',
        contactNumber: '-',
        length: undefined,
        width: undefined,
        depth: undefined,
        load: undefined,
        remark: '可查询航迹记录'
      };
      
      this.currentShip = basicShip;
      this.shipDialogVisible = true;
      this.$message.info('已显示船舶信息面板，可查询航迹');
      this.isSearching = false;
    },
    
    // 预警信息相关方法
    toggleNoticeContent() {
      this.noticeContentVisible = !this.noticeContentVisible;
    },
    
    toggleVhf() {
      this.vhfEnabled = !this.vhfEnabled;
    },
    
    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
    },
    
    clearNotices() {
      this.warningNotices = [];
      this.navigationNotices = [];
      this.noticeCount = 0;
      this.warningCount = 0;
      this.navigationCount = 0;
    },
    
    removeNotice(id) {
      this.warningNotices = this.warningNotices.filter(notice => notice.id !== id);
      this.navigationNotices = this.navigationNotices.filter(notice => notice.id !== id);
      
      // 更新计数
      this.warningCount = this.warningNotices.length;
      this.navigationCount = this.navigationNotices.length;
      this.noticeCount = this.warningCount + this.navigationCount;
    },
    
    showShipTrack(mmsi, timeRangeData = null) {
      console.log('显示船舶轨迹:', mmsi, '时间范围数据:', timeRangeData);
      
      if (!mmsi || mmsi.trim() === '') {
        this.$message.warning('船舶MMSI无效，无法查看轨迹');
        return;
      }
      
             // 设置时间范围
       if (timeRangeData && timeRangeData.startTime && timeRangeData.endTime) {
         // 使用传入的时间范围
         const startTime = new Date(timeRangeData.startTime);
         const endTime = new Date(timeRangeData.endTime);
         
         this.trackTimeRange = [
           this.formatDateTime(startTime),
           this.formatDateTime(endTime)
         ];
         
         // 保存查询模式信息
         this.trackQueryMode = {
           useLatestRecords: timeRangeData.useLatestRecords || false,
           maxRecords: timeRangeData.maxRecords || 0
         };
         
         console.log('使用传入的时间范围:', {
           startTime: timeRangeData.startTime,
           endTime: timeRangeData.endTime,
           formattedRange: this.trackTimeRange,
           queryMode: this.trackQueryMode
         });
       } else {
         // 预设航迹时间范围为最近24小时，取最后10条数据
         const endTime = new Date();
         const startTime = new Date(endTime.getTime() - 24 * 60 * 60 * 1000); // 24小时前
         
         this.trackTimeRange = [
           this.formatDateTime(startTime),
           this.formatDateTime(endTime)
         ];
         
         // 默认查询模式
         this.trackQueryMode = {
           useLatestRecords: true,
           maxRecords: 10
         };
         
         console.log('使用默认时间范围（最近24小时，最后10条）:', this.trackTimeRange);
       }
      
      // 设置当前船舶MMSI
      this.currentShipMmsi = String(mmsi);
      
      // 尝试通过MMSI查找船舶信息用于显示船舶名称等
      const ship = this.ships.find(ship => ship.userid === mmsi);
      if (ship) {
        this.currentShip = ship;
        console.log('找到船舶信息，设置为当前船舶:', ship.shipname || ship.userid);
      } else {
        // 即使没有找到船舶信息，也创建一个基本的船舶对象
        this.currentShip = {
          userid: mmsi,
          shipname: mmsi,
          pos: null
        };
        console.log('未找到船舶信息，使用MMSI创建基本船舶对象');
      }
      
      // 保存当前船舶图层状态并隐藏船舶图层
      if (this.shipLayerStateBeforeTrack === null) {
        this.shipLayerStateBeforeTrack = this.layerStatus.ship;
        console.log('保存显示航迹前的船舶图层状态:', this.shipLayerStateBeforeTrack);
        
        // 如果船舶图层当前是显示状态，则隐藏它
        if (this.layerStatus.ship) {
          this.layerStatus.ship = false;
          this.toggleShipVisibility(false);
          console.log('隐藏船舶图层以显示航迹');
        }
      }
      
      // 显示航迹查看器
      this.showTrackViewer = true;
      
      const modeText = this.trackQueryMode.useLatestRecords ? 
        `最后${this.trackQueryMode.maxRecords}条记录` : '时间范围内所有记录';
      console.log(`开始显示船舶 ${mmsi} 的轨迹数据，时间范围: ${this.trackTimeRange[0]} 至 ${this.trackTimeRange[1]}，查询模式: ${modeText}`);
    },
    
    locateShipById(mmsi, options = {}) {
      console.log(`尝试定位船舶，MMSI: ${mmsi}`, options);
      
      // 解构选项参数
      const { selectShip = false, showDetails = false } = options;
      
      // 查找船舶
      const ship = this.ships.find(ship => ship.userid === mmsi);
      
      if (ship && ship.pos) {
        // 如果找到船舶并且有位置信息
        const position = {
          lat: ship.pos.latitude,
          lng: ship.pos.longitude
        };
        
        // 设置地图视图中心点和缩放级别
        if (this.map) {
          this.map.setView([position.lat, position.lng], 16);
          
          // 高亮显示该船舶
          this.highlightShip(ship);
          
          // 如果需要选中并显示详情面板
          if (selectShip && showDetails) {
            // 显示船舶详情面板
            this.showShipInfo(ship);
          }
        } else {
          // 如果地图未初始化，则设置中心点和缩放级别
          this.center = [position.lat, position.lng];
          this.zoom = 16;
        }
        
        // 显示成功消息
        const shipName = ship.cnName || ship.shipname || ship.userid;
        this.$message.success(`已定位到船舶 ${shipName}`);
        
        return true;
      } else if (this.currentShipMmsi === mmsi && this.showTrackViewer) {
        // 如果当前正在查看该船舶的航迹，尝试使用航迹点定位
        console.log('船舶未找到，但正在查看其航迹，尝试使用航迹点定位');
        
        // 直接调用ShipTrackViewer组件的locateShip方法
        if (this.$refs.trackViewer) {
          this.$refs.trackViewer.locateShip();
          return true;
        }
        
        return false;
      } else {
        // 如果找不到船舶或位置信息
        this.$message.warning(`未找到船舶 ${mmsi} 或无位置信息`);
        return false;
      }
    },
    
    // 高亮显示船舶（可选实现）
    highlightShip(ship) {
      // 如果有船舶图层，可以在这里实现高亮效果
      console.log('高亮显示船舶:', ship.userid);
      
      // 清除之前的高亮效果
      this.clearHighlight();
      
      // 保存当前高亮的船舶
      this.currentHighlightShip = ship;
      
      // 实现示例：如果使用Canvas图层，可以添加临时高亮效果
      if (this.map && ship.pos) {
        try {
          const position = [ship.pos.latitude, ship.pos.longitude];
          
          // 创建一个临时标记
          this.highlightMarker = L.circleMarker(position, {
            radius: 25,  // 增大半径
            color: '#ff0000',
            weight: 3,   // 增加边框宽度
            opacity: 0.9,
            fillColor: '#ff7800',
            fillOpacity: 0.4,
            className: 'ship-highlight-marker',
            // 确保高亮标记在船舶图层之上
            pane: 'markerPane', // 使用Leaflet的markerPane，它通常在其他图层之上
            zIndexOffset: 1000   // 使用zIndexOffset而不是setZIndex
          }).addTo(this.map);
          
          // 添加闪烁效果
          let opacity = 0.9;
          let increasing = false;
          this.highlightInterval = setInterval(() => {
            if (increasing) {
              opacity += 0.1;
              if (opacity >= 0.9) {
                opacity = 0.9;
                increasing = false;
              }
            } else {
              opacity -= 0.1;
              if (opacity <= 0.4) {
                opacity = 0.4;
                increasing = true;
              }
            }
            
            if (this.highlightMarker) {
              this.highlightMarker.setStyle({ opacity: opacity });
            } else {
              if (this.highlightInterval) {
                clearInterval(this.highlightInterval);
                this.highlightInterval = null;
              }
            }
          }, 100);
          
        } catch (error) {
          console.error('高亮显示船舶时出错:', error);
        }
      }
    },
    
    // 清除高亮效果
    clearHighlight() {
      // 清除闪烁效果的定时器
      if (this.highlightInterval) {
        clearInterval(this.highlightInterval);
        this.highlightInterval = null;
      }
      
      // 移除高亮标记
      if (this.map && this.highlightMarker) {
        this.map.removeLayer(this.highlightMarker);
        this.highlightMarker = null;
      }
      
      // 清除当前高亮船舶引用
      this.currentHighlightShip = null;
    },
    
    showNoticeDetail(notice) {
      // 显示预警信息详情，使用新面板组件
      const alertData = {
        id: notice.id,
        alertTime: notice.time,
        alertLevel: notice.level,
        alertMessage: notice.message || notice.description,
        sourceType: notice.sourceType || 'AIS',
        sourceId: notice.mmsi,
        sourceName: notice.shipName,
        ruleType: notice.type,
        status: notice.status || 0
      };
      
      this.showAlertDetail(alertData);
    },
    
    getNoticeTypeName(type) {
      const typeMap = {
        'deviation': '偏航',
        'height': '超高',
        'speed': '超速',
        'stay': '滞留',
        'navigation': '通航'
      };
      return typeMap[type] || type;
    },
    
    // 其他功能方法
    loadMockLayerData() {
      // 加载模拟图层数据
      if (loadMockLayerData) {
        const layerData = loadMockLayerData();
        this.shipTracks = layerData.shipTracks;
        // 不再从模拟数据加载警戒区域
        // this.warningAreas = layerData.warningAreas;
        
        // 初始化一个空的警戒区域数组
        this.warningAreas = [];
        this.polygonWarningAreas = [];
      } else {
        console.log('加载模拟图层数据');
        this.shipTracks = [];
        this.warningAreas = [];
        this.polygonWarningAreas = [];
      }
    },
    
    createShipTracksLayer() {
      // 创建船舶航迹线图层
      if (this.map && createShipTracksLayer) {
        this.shipTracksLayerGroup = createShipTracksLayer(
          this.map,
          this.shipTracks,
          (track) => {
            console.log('点击航迹:', track.shipId);
          }
        );
        
        // 确保航迹图层在区域图层之上
        if (this.shipTracksLayerGroup) {
          this.shipTracksLayerGroup.setZIndex(150); // 统一使用150
        }
      } else {
        console.log('创建船舶航迹图层');
      }
    },
    
    createWarningAreasLayer() {
      // 创建警戒区域图层但不直接添加到地图
      console.log('创建警戒区域图层 (不添加到地图)');
      
      if (!this.map) {
        console.error('无法创建警戒区域图层：地图未初始化');
        return L.layerGroup(); // 返回空图层而非null
      }
      
      // 检查warningAreas是否存在且有数据
      if (!this.warningAreas || !Array.isArray(this.warningAreas)) {
        console.warn('警戒区域数组未初始化或格式不正确，初始化为空数组');
        this.warningAreas = [];
        this.polygonWarningAreas = [];
        return L.layerGroup();
      }
      
      if (this.warningAreas.length === 0) {
        console.warn('警戒区域数组为空');
        return L.layerGroup();
      }
      
      try {
        // 使用createWarningAreasLayer函数创建图层
        if (typeof createWarningAreasLayer === 'function') {
          console.log('使用工具函数创建区域图层，区域数量:', this.polygonWarningAreas.length);
          // 创建图层但不添加到地图
          const layerGroup = createWarningAreasLayer(
            this.map,
            this.polygonWarningAreas,
            (area) => {
              console.log('点击警戒区域:', area.id);
            }
          );
          
          // 如果成功创建图层
          if (layerGroup) {
            // 添加识别标记
            layerGroup._leaflet_id = 'warningAreasLayerGroup_' + Date.now();
            layerGroup.name = 'warningAreasLayerGroup';
            layerGroup.isWarningAreaGroup = true;
            
            // 设置默认的z-index
            layerGroup.setZIndex(50);
            return layerGroup;
          }
        }
        
        // 如果工具函数不可用或创建失败，使用备用方法
        console.warn('工具函数不可用，使用备用方法创建区域图层');
        return this.createBackupAreaLayer(false); // 不自动添加到地图
      } catch (error) {
        console.error('创建警戒区域图层时出错:', error);
        return L.layerGroup(); // 返回一个空图层
      }
    },
    
    updateShipMarkers() {
      // 如果Canvas图层未初始化，跳过更新
      if (!this.canvasLayer || !this.layerInitStatus.canvasInitialized) {
        console.warn('Canvas图层未初始化，跳过更新船舶标记');
        return;
      }

      // 如果正在查看航迹，也跳过更新
      if (this.showTrackViewer) {
        return;
      }

      // 缩放动画期间节流：SignalR 推送的 AIS 更新在缩放动画中全量重建船舶标记
      // + Canvas 重绘，在最大缩放级别下可阻塞主线程数百毫秒（profile trace 确认
      // webSocket.onmessage 单次执行 606ms）。动画期间跳过一次，等动画结束后
      // 通过 zoomend 触发的 updateShipMarkers 再补一次。
      if (this.map && this.map._animatingZoom) {
        return;
      }

      try {
        // 确保船舶数据和图标存在
        if (!this.ships || !Array.isArray(this.ships) || this.ships.length === 0 || !this.shipIcon) {
          console.warn('船舶数据或图标不可用，跳过更新船舶标记');
          return;
        }
        
        // 确保Canvas图层有必要的方法
        if (!this.canvasLayer.addLayers || !this.canvasLayer.removeLayers) {
          console.warn('Canvas图层缺少必要方法，跳过更新');
          return;
        }
        
        // console.log('开始更新船舶标记，当前ship数量:', this.ships.length);
        
        // 移除现有标记
        if (this.shipMarkers && this.shipMarkers.length > 0) {
          try {
            // console.log('移除现有标记数量:', this.shipMarkers.length);
            this.canvasLayer.removeLayers(this.shipMarkers);
          } catch (e) {
            console.warn('移除现有标记失败:', e);
          }
          this.shipMarkers = [];
        }
        
        // 获取桥梁中心点
        const bridgeCenter = this.getBridgeCenter();
        const displayRadius = appConfig.ship.displayRadius;
        
        // 构建当前未处理告警的 MMSI 集合，用于船舶图标视觉区分
        const alertMmsiSet = new Set();
        if (this.activeAlerts && this.activeAlerts.length > 0) {
          this.activeAlerts.forEach(alert => {
            if (alert.vesselMmsi) {
              alertMmsiSet.add(alert.vesselMmsi);
            }
          });
        }
        
        // 使用工具函数创建新的船舶标记
        if (updateShipMarkers) {
          this.shipMarkers = updateShipMarkers(
            this.canvasLayer,
            this.ships,
            this.shipIcon,
            bridgeCenter,
            displayRadius,
            alertMmsiSet
          );
          // console.log('成功创建新船舶标记，数量:', this.shipMarkers.length);
        } else {
          console.error('updateShipMarkers函数不可用');
        }
      } catch (e) {
        console.error('更新船舶标记时出错:', e);
      }
    },
    
    /**
     * 处理桥梁选择变更事件
     * @param {Object} bridgeData 桥梁数据
     */
    handleBridgeChanged(bridgeData) {
      console.log('桥梁选择变更，重新过滤船舶显示:', bridgeData);
      // 延迟更新船舶标记，确保桥梁信息已更新
      this.$nextTick(() => {
        this.updateShipMarkers();
      });
    },
    
    
    /**
     * 获取桥梁中心点坐标
     * @returns {Object|null} 桥梁中心点坐标 {lat, lng} 或 null
     */
    getBridgeCenter() {
      // 优先从bridgeInfo prop获取位置
      if (this.bridgeInfo && this.bridgeInfo.latitude && this.bridgeInfo.longitude) {
        return {
          lat: this.bridgeInfo.latitude,
          lng: this.bridgeInfo.longitude
        };
      }
      
      // 备选：从localStorage读取选中的桥梁位置
      try {
        const selectedBridge = localStorage.getItem('selectedBridge');
        if (selectedBridge) {
          const bridgeData = JSON.parse(selectedBridge);
          if (bridgeData.latitude && bridgeData.longitude) {
            return {
              lat: bridgeData.latitude,
              lng: bridgeData.longitude
            };
          }
        }
      } catch (error) {
        console.warn('解析localStorage中的桥梁数据失败:', error);
      }
      
      // 如果都没有，使用地图中心点
      if (this.map) {
        const center = this.map.getCenter();
        return {
          lat: center.lat,
          lng: center.lng
        };
      }
      
      console.warn('无法获取桥梁中心点，将显示所有船舶');
      return null;
    },
    
    showShipInfo(ship) {
      console.log('显示船舶信息:', ship);
      if (!ship) return;
      
        // 添加防抖处理
        if(ship.handleShipClick){
          return;
        }
        ship.handleShipClick = true;
        setTimeout(() => {
          ship.handleShipClick = false;  
        }, 500);
        
      
      this.currentShip = ship;
      this.shipPanelVisible = true;
      
      // 如果有MMSI，尝试从API获取更详细的船舶信息
      if (ship.userid) {
        this.fetchVesselDetailByMmsi(ship.userid);
      }
      
      // 高亮显示船舶
      if (ship.pos) {
        this.highlightShip(ship);
      }
      
      console.log('船舶信息面板已显示');
    },
    
    // 格式化方法
    formatCog(cog) {
      return formatCogUtil ? formatCogUtil(cog) : (cog !== undefined ? cog.toFixed(1) : '0.0');
    },
    
    formatSog(sog) {
      return formatSogUtil ? formatSogUtil(sog) : (sog !== undefined ? sog.toFixed(1) : '0.0');
    },
    
    // 编辑船舶信息
    editBridge(bridge, e) {
      // 阻止事件冒泡，避免点击编辑按钮时关闭popup
      if (e) e.stopPropagation();
      console.log('编辑桥梁:', bridge);
    },
    
    changeMapType(type) {
      // 更新当前地图类型
      this.currentMapType = type;
      
      // 通过BaseMap组件调用天地图API切换地图类型
      if (this.$refs && this.$refs.baseMap) {
        this.$refs.baseMap.changeMapType(type, this.tiandituKey);
      }
      
      // 更新基础底图状态
      if (handleMapTypeChange) {
        this.layerStatus = handleMapTypeChange(type, this.layerStatus);
      }
    },
    
    // 处理桥梁变更事件
    handleBridgeChange(bridge) {
      console.log('地图组件接收到桥梁变更:', bridge);
      if (!bridge) {
        console.warn('接收到的桥梁变更事件不包含桥梁信息');
        return;
      }
      
      if (!bridge.id) {
        console.warn('接收到的桥梁变更事件不包含有效的桥梁ID');
        return;
      }
      
      // 在处理新的桥梁选择之前，先清除当前的区域图层
      console.log('【ShipMapView】单桥梁选择变更，清除现有区域图层');
      this.forceRemoveAllWarningAreaLayers();
      
      // 检查桥梁对象是否包含位置信息
      if (bridge.latitude && bridge.longitude) {
        // 直接使用传入的位置信息设置地图中心点
        const lat = parseFloat(bridge.latitude);
        const lng = parseFloat(bridge.longitude);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          console.log(`直接设置地图中心到桥梁位置: [${lat}, ${lng}]`);
          
          // 设置地图中心点
          if (this.map) {
            this.map.setView([lat, lng], 15);
          } else {
            this.center = [lat, lng];
            this.zoom = 15;
          }
          
          // 注意：区域数据的加载现在由 handleBridgesSelectionChange 统一处理
          // 这里只处理地图中心点设置，避免重复加载区域数据
          console.log('【ShipMapView】单桥梁变更事件已处理，区域数据将由多桥梁选择事件统一加载');
          
          // 旧的区域加载逻辑（已禁用，由多桥梁选择机制统一处理）
          // this.loadBridgeAreas(bridge.id);
          return;
        }
      }
      
      // 如果没有位置信息，也不再加载区域数据
      // 区域数据的加载统一由 handleBridgesSelectionChange 处理
      console.log('【ShipMapView】桥梁对象不包含位置信息，跳过处理（区域数据由多桥梁选择事件统一加载）');
      
      // 旧的备用逻辑（已禁用）
      // this.loadBridgeDetails(bridge.id);
    },
    
    // 处理多桥梁选择变更事件
    handleBridgesSelectionChange(selectionData) {
      console.log('【ShipMapView】地图组件接收到多桥梁选择变更:', selectionData);
      
      if (!selectionData) {
        console.warn('接收到的多桥梁选择事件不包含选择数据');
        return;
      }
      
      const { selectedBridges, selectedBridgeIds, primaryBridge } = selectionData;
      
      // 在处理新的桥梁选择之前，先清除当前的区域图层
      console.log('【ShipMapView】桥梁选择变更，清除现有区域图层');
      this.forceRemoveAllWarningAreaLayers();
      
      // 更新组件状态
      this.selectedBridges = selectedBridges || [];
      this.selectedBridgeIds = selectedBridgeIds || [];
      
      console.log(`多桥梁选择更新: 选择了 ${this.selectedBridges.length} 个桥梁`, this.selectedBridges.map(b => b.name));
      
      // 处理地图中心点和区域加载
      if (primaryBridge) {
        // 使用主要桥梁设置地图中心点（保持与单桥梁选择的兼容性）
        if (primaryBridge.latitude && primaryBridge.longitude) {
          const lat = parseFloat(primaryBridge.latitude);
          const lng = parseFloat(primaryBridge.longitude);
          
          if (!isNaN(lat) && !isNaN(lng)) {
            console.log(`设置地图中心到主要桥梁位置: [${lat}, ${lng}]`);
            
            if (this.map) {
              this.map.setView([lat, lng], 15);
            } else {
              this.center = [lat, lng];
              this.zoom = 15;
            }
          }
        }
      }
      
      // 加载所有选中桥梁的区域数据
      if (this.selectedBridgeIds && this.selectedBridgeIds.length > 0) {
        console.log('开始加载多个桥梁的区域数据，桥梁IDs:', this.selectedBridgeIds);
        this.loadMultipleBridgeAreas(this.selectedBridgeIds);
      } else {
        console.log('没有选中的桥梁，清空区域数据');
        // 清空区域数据
        this.warningAreas = [];
        this.polygonWarningAreas = [];
        this.areaDataBackup = [];
        // 重新初始化空的警戒区域图层
        this.initWarningAreaLayer(true);
      }
    },
    
    // 加载桥梁详情
    async loadBridgeDetails(bridgeId) {
      console.log('正在加载桥梁详情, ID:', bridgeId);
      try {
        const response = await getBridgeById(bridgeId);
        if (response && response.code === 200 && response.data) {
          const bridgeData = response.data;
          console.log('桥梁详情:', bridgeData);
          
          // 如果有经纬度，将地图居中到桥梁位置
          if (bridgeData.latitude && bridgeData.longitude) {
            const lat = parseFloat(bridgeData.latitude);
            const lng = parseFloat(bridgeData.longitude);
            
            if (!isNaN(lat) && !isNaN(lng)) {
              console.log(`将地图居中到桥梁位置: [${lat}, ${lng}]`);
              if (this.map) {
                this.map.setView([lat, lng], 15);
              } else {
                this.center = [lat, lng];
                this.zoom = 15;
              }
              
              // 更新localStorage中的数据：获取当前缓存的桥梁信息，并添加位置信息
              try {
                const savedBridgeJson = localStorage.getItem('selectedBridge');
                if (savedBridgeJson) {
                  const savedBridge = JSON.parse(savedBridgeJson);
                  // 将位置信息合并到现有的桥梁对象中
                  const updatedBridge = {
                    ...savedBridge,
                    latitude: bridgeData.latitude,
                    longitude: bridgeData.longitude,
                    // 可选：添加其他需要的桥梁字段
                    height: bridgeData.height,
                    bridgeType: bridgeData.bridgeType
                  };
                  // 保存回localStorage
                  localStorage.setItem('selectedBridge', JSON.stringify(updatedBridge));
                  console.log('已更新localStorage中桥梁的位置信息');
                }
              } catch (e) {
                console.warn('更新localStorage中桥梁位置信息失败', e);
              }
            }
          }
          
          // 加载桥梁区域信息
          this.loadBridgeAreas(bridgeId);
        } else {
          console.error('加载桥梁详情失败:', response);
          // 即使桥梁详情加载失败，也尝试加载区域数据
          this.loadBridgeAreas(bridgeId);
        }
      } catch (error) {
        console.error('加载桥梁详情出错:', error);
        // 出错时仍然尝试加载区域数据
        this.loadBridgeAreas(bridgeId);
      }
    },
    
    // 加载桥梁的区域数据
    async loadBridgeAreas(bridgeId) {
      if (!bridgeId) {
        console.warn('尝试加载区域数据时未提供bridgeId');
        this.warningAreas = [];
        this.polygonWarningAreas = [];
        this.areaDataBackup = [];
        return;
      }
      
      try {
        console.log('开始加载区域数据，桥梁ID:', bridgeId);
        
        // 尝试加载区域数据，最多重试2次
        let response = null;
        let retryCount = 0;
        const maxRetries = 2;
        
        while (retryCount <= maxRetries && (!response || response.code !== 200)) {
          try {
            response = await getAreasByBridgeId(bridgeId);
            if (response && response.code === 200) break;
            
            // 如果请求成功但状态码不是200，不再重试
            if (response) break;
            
            retryCount++;
            if (retryCount <= maxRetries) {
              console.log(`区域数据加载失败，尝试重试(${retryCount}/${maxRetries})...`);
              await new Promise(resolve => setTimeout(resolve, 1000)); // 等待1秒再重试
            }
          } catch (retryError) {
            console.error('加载区域数据出错，尝试重试:', retryError);
            retryCount++;
            if (retryCount > maxRetries) throw retryError;
            await new Promise(resolve => setTimeout(resolve, 1000)); // 等待1秒再重试
          }
        }
        
        if (response && response.code === 200 && Array.isArray(response.data)) {
          console.log('区域数据加载成功，数量:', response.data.length);
          
          // 清除现有区域图层
          if (this.warningAreasLayerGroup && this.map) {
            if (this.map.hasLayer(this.warningAreasLayerGroup)) {
              this.map.removeLayer(this.warningAreasLayerGroup);
            }
            this.warningAreasLayerGroup = null;
          }
          
          // 跟踪过滤过程中的错误，以便后续聚合输出
          let noCoordinatesCount = 0;
          let insufficientPointsCount = 0;
          let parseErrorCount = 0;
          
          // 处理和验证区域数据
          const processedAreas = response.data
            .filter(area => {
              if (!area || !area.coordinates) {
                noCoordinatesCount++;
                return false;
              }
              return true;
            })
            .map(area => {
              try {
                // 使用mapUtils工具解析坐标
                const coordinates = mapUtils.parseAreaCoordinates(area);
                
                // 验证坐标是否有效 - 考虑不同几何类型的最小点数要求
                const geometryType = area.geometryType || 'POLYGON';
                const minPointsRequired = geometryType === 'POINT' ? 1 : 2;
                
                if (!coordinates || coordinates.length < minPointsRequired) {
                  insufficientPointsCount++;
                  return null;
                }
                
                // 根据名称推断区域类型
                const inferredType = this.inferAreaType(area.name || area.areaName || '');
                
                return {
                  id: area.id,
                  name: area.name || area.areaName || '未命名区域',
                  type: inferredType, // 使用推断的类型
                  originalType: area.areaType, // 保留原始类型
                  geometryType: geometryType, // 保存几何类型信息
                  // 以下属性是为了mapLayerRenderer.js中的创建多边形方法
                  coordinates: coordinates,
                  points: coordinates,
                  color: area.color,
                  borderColor: area.color,
                  icon: area.icon || area.iconName,
                  borderWidth: 2
                };
              } catch (error) {
                parseErrorCount++;
                console.error('解析区域坐标失败:', error, area);
                return null;
              }
            })
            .filter(Boolean); // 过滤掉null值
          
          // 聚合错误统计
          if (noCoordinatesCount > 0 || insufficientPointsCount > 0 || parseErrorCount > 0) {
            console.warn(`区域数据过滤结果：${processedAreas.length}/${response.data.length} 个区域有效，` +
                         `${noCoordinatesCount} 个无坐标，` +
                         `${insufficientPointsCount} 个点数不足，` +
                         `${parseErrorCount} 个解析错误`);
          } else {
            console.log(`所有区域数据有效，数量: ${processedAreas.length}`);
          }
          
          // 分离点标注和其他区域，避免mapLayerRenderer处理点标注
          const polygonAreas = processedAreas.filter(area => area.geometryType !== 'POINT');
          
          // 设置警戒区域数据 (包含所有类型，供computed使用)
          this.warningAreas = processedAreas;
          
          // 设置非点标注的区域数据 (仅供mapLayerRenderer使用)
          this.polygonWarningAreas = polygonAreas;
          
          // 备份区域数据
          this.areaDataBackup = JSON.parse(JSON.stringify(processedAreas));
          console.log(`区域数据已备份，数量: ${this.areaDataBackup.length}`);
          
          // 记录警戒区域加载状态
          console.log(`成功加载警戒区域: ${this.warningAreas.length}个区域`);
          
          // 如果有区域数据且设置为显示状态，则通过统一方法创建并显示图层
          if (this.warningAreas.length > 0) {
            console.log('加载完成，初始化警戒区域图层');
            this.layerStatus.warningAreas = true;
            this.initWarningAreaLayer();
          } else {
            console.warn('没有找到有效的警戒区域数据');
            this.$message.info('当前桥梁没有警戒区域数据');
            // 即使没有数据，也要初始化空的图层以便后续切换
            this.initWarningAreaLayer(true);
          }
          
          // 刷新地图
          if (this.map) {
            setTimeout(() => {
              try {
                this.map.invalidateSize();
              } catch (e) {
                console.warn('地图刷新失败，但不影响使用', e);
              }
            }, 200);
          }
        } else {
          console.error('加载区域数据失败:', response?.message || '未知错误');
          this.warningAreas = [];
          this.polygonWarningAreas = [];
          this.areaDataBackup = [];
        }
      } catch (error) {
        console.error('加载区域数据失败:', error);
        this.warningAreas = [];
        this.polygonWarningAreas = [];
        this.areaDataBackup = [];
      }
    },
    
    // 加载多个桥梁的区域数据
    async loadMultipleBridgeAreas(bridgeIds) {
      if (!bridgeIds || bridgeIds.length === 0) {
        console.warn('尝试加载多桥梁区域数据时未提供bridgeIds');
        this.warningAreas = [];
        this.polygonWarningAreas = [];
        this.areaDataBackup = [];
        return;
      }
      
      console.log('开始加载多个桥梁的区域数据，桥梁IDs:', bridgeIds);
      
      try {
        // 并行加载所有桥梁的区域数据
        const loadPromises = bridgeIds.map(async (bridgeId) => {
          try {
            // 确保bridgeId是数字类型
            const numericBridgeId = typeof bridgeId === 'string' ? parseInt(bridgeId) : bridgeId;
            console.log(`正在加载桥梁 ${numericBridgeId} 的区域数据...`);
            
            const response = await getAreasByBridgeId(numericBridgeId);
            if (response && response.code === 200 && response.data) {
              console.log(`成功加载桥梁 ${numericBridgeId} 的区域数据，数量: ${response.data.length}`);
              return {
                bridgeId: numericBridgeId,
                areas: response.data
              };
            } else {
              console.warn(`桥梁 ${numericBridgeId} 没有区域数据或API返回异常:`, response);
            }
            return null;
          } catch (error) {
            console.error(`加载桥梁 ${bridgeId} 的区域数据失败:`, error);
            return null;
          }
        });
        
        const results = await Promise.all(loadPromises);
        
        // 合并所有桥梁的区域数据
        let allAreas = [];
        results.forEach(result => {
          if (result && result.areas) {
            // 为每个区域添加桥梁ID标识
            const areasWithBridgeId = result.areas.map(area => ({
              ...area,
              sourceBridgeId: result.bridgeId
            }));
            allAreas = allAreas.concat(areasWithBridgeId);
          }
        });
        
        console.log(`成功合并 ${bridgeIds.length} 个桥梁的区域数据，总数量: ${allAreas.length}`);
        
        if (allAreas.length > 0) {
          // 处理合并后的区域数据（使用与单桥梁相同的逻辑）
          this.processAndDisplayAreas(allAreas, true);
        } else {
          console.warn('没有找到任何有效的区域数据');
          this.warningAreas = [];
          this.polygonWarningAreas = [];
          this.areaDataBackup = [];
          this.initWarningAreaLayer(true); // 初始化空的图层
        }
        
      } catch (error) {
        console.error('加载多桥梁区域数据失败:', error);
        this.warningAreas = [];
        this.polygonWarningAreas = [];
        this.areaDataBackup = [];
      }
    },
    
    // 处理和显示区域数据（提取公共逻辑）
    processAndDisplayAreas(rawAreas, isMultipleBridges = false) {
      console.log('【processAndDisplayAreas】开始处理区域数据，区域数量:', rawAreas.length, '多桥梁模式:', isMultipleBridges);
      
      // 清除现有区域图层，避免重复绘制
      this.forceRemoveAllWarningAreaLayers();
      
      // 处理区域数据逻辑（与原来loadBridgeAreas中的处理逻辑相同）
      const processedAreas = rawAreas
        .map(area => {
          if (!area.coordinates || area.coordinates.trim() === '') {
            console.warn('区域坐标为空:', area);
            return null;
          }
          
          try {
            // 使用mapUtils工具解析坐标（与原始方法保持一致）
            const coordinates = mapUtils.parseAreaCoordinates(area);
            
            // 验证坐标是否有效 - 考虑不同几何类型的最小点数要求
            const geometryType = area.geometryType || 'POLYGON';
            const minPointsRequired = geometryType === 'POINT' ? 1 : 2;
            
            if (!coordinates || coordinates.length < minPointsRequired) {
              console.warn('坐标点数不足:', area);
              return null;
            }
            
            // 根据名称推断区域类型
            const inferredType = this.inferAreaType(area.name || area.areaName || '');
            
            return {
              id: area.id,
              name: area.name || area.areaName || '未命名区域',
              type: inferredType, // 使用推断的类型
              originalType: area.areaType, // 保留原始类型
              geometryType: geometryType, // 保存几何类型信息
              // 以下属性是为了mapLayerRenderer.js中的创建多边形方法
              coordinates: coordinates,
              points: coordinates,
              color: area.color,
              borderColor: area.color,
              icon: area.icon || area.iconName,
              borderWidth: 2,
              sourceBridgeId: area.sourceBridgeId // 保留桥梁来源标识
            };
          } catch (error) {
            console.error('解析区域坐标失败:', error, area);
            return null;
          }
        })
        .filter(Boolean);
      
      // 分离点标注和多边形区域
      const polygonAreas = processedAreas.filter(area => area.geometryType !== 'POINT');
      
      // 设置警戒区域数据
      this.warningAreas = processedAreas;
      this.polygonWarningAreas = polygonAreas;
      this.areaDataBackup = JSON.parse(JSON.stringify(processedAreas));
      
      // 记录加载状态
      const bridgeText = isMultipleBridges ? '多个桥梁' : '当前桥梁';
      console.log(`成功加载${bridgeText}的警戒区域: ${this.warningAreas.length}个区域`);
      
      // 显示区域图层
      if (this.warningAreas.length > 0) {
        console.log('加载完成，初始化警戒区域图层');
        this.layerStatus.warningAreas = true;
        this.initWarningAreaLayer();
      } else {
        console.warn(`${bridgeText}没有找到有效的警戒区域数据`);
        this.initWarningAreaLayer(true);
      }
      
      // 刷新地图
      if (this.map) {
        this.map.invalidateSize();
      }
    },
    
    // 根据名称推断区域类型
    inferAreaType(name) {
      if (!name) return 'OTHER';
      
      const nameUpper = name.toUpperCase();
      
      // 根据名称关键词推断类型
      if (nameUpper.includes('警戒') || nameUpper.includes('WARNING')) {
        return 'WARNING';
      } else if (nameUpper.includes('桥') || nameUpper.includes('BRIDGE')) {
        return 'BRIDGE';
      } else if (nameUpper.includes('监控') || nameUpper.includes('摄像') || nameUpper.includes('CAMERA')) {
        return 'CAMERA';
      } else if (nameUpper.includes('雷达') || nameUpper.includes('RADAR')) {
        return 'RADAR_AREA';
      } else if (nameUpper.includes('航道') || nameUpper.includes('CHANNEL')) {
        return 'CHANNEL';
      } else if (nameUpper.includes('禁航') || nameUpper.includes('限制')) {
        return 'RESTRICTED';
      } else if (nameUpper.includes('通航') || nameUpper.includes('NAVIGATION')) {
        return 'NAVIGATION';
      } else if (nameUpper.includes('点') && !nameUpper.includes('警戒')) {
        return 'POINT_MARKER';
      }
      
      return 'OTHER';
    },

    // 获取区域图标
    getAreaIcon(areaType) {
      // 根据区域类型返回不同的图标
      const iconMap = {
        'BRIDGE': 'el-icon-place',
        'RESTRICTED': 'el-icon-warning',
        'WARNING': 'el-icon-warning-outline',
        'CHANNEL': 'el-icon-location',
        'NAVIGATION': 'el-icon-guide',
        'REPORT_LINE_UP': 'el-icon-position',
        'REPORT_LINE_DOWN': 'el-icon-position',
        'WARNING_LINE_UP': 'el-icon-warning',
        'WARNING_LINE_DOWN': 'el-icon-warning',
        'RADAR_AREA_UP': 'el-icon-view',
        'RADAR_AREA_DOWN': 'el-icon-view',
        'RADAR_AREA': 'el-icon-view',
        'NO_NAVIGATION': 'el-icon-circle-close',
        'BRIDGE_MAST': 'el-icon-place',
        'CAMERA': 'el-icon-video-camera',
        'POINT_MARKER': 'el-icon-location-outline',
        'OTHER': 'el-icon-info'
      };
      
      return iconMap[areaType] || 'el-icon-location'; // 默认位置图标
    },
    
    // // 获取区域颜色
    // getAreaColor(areaType) {
    //   // 根据区域类型返回不同的填充颜色
    //   const colorMap = {
    //     'BRIDGE': '#42b983',           // 桥梁区域 - 绿色
    //     'RESTRICTED': '#f56c6c',       // 限制区域 - 红色
    //     'WARNING': '#e6a23c',          // 警戒区域 - 黄色
    //     'CHANNEL': '#409EFF',          // 航道区域 - 蓝色
    //     'NAVIGATION': '#67C23A',       // 通航区域 - 绿色
    //     'REPORT_LINE_UP': '#9254de',   // 上游报告线 - 紫色
    //     'REPORT_LINE_DOWN': '#9254de', // 下游报告线 - 紫色
    //     'WARNING_LINE_UP': '#ff9f43',  // 上游警戒线 - 橙色
    //     'WARNING_LINE_DOWN': '#ff9f43' // 下游警戒线 - 橙色
    //   };
      
    //   return colorMap[areaType] || '#42b983'; // 默认绿色
    // },
    
    // 获取区域边框颜色
    getAreaBorderColor(areaType) {
      // 根据区域类型返回不同的边框颜色
      const borderColorMap = {
        'BRIDGE': '#2c9d70',           // 桥梁区域 - 深绿色
        'RESTRICTED': '#dc3545',       // 限制区域 - 深红色
        'WARNING': '#cc8214',          // 警戒区域 - 深黄色
        'CHANNEL': '#0d6efd',          // 航道区域 - 深蓝色
        'NAVIGATION': '#198754',       // 通航区域 - 深绿色
        'REPORT_LINE_UP': '#6610f2',   // 上游报告线 - 深紫色
        'REPORT_LINE_DOWN': '#6610f2', // 下游报告线 - 深紫色
        'WARNING_LINE_UP': '#fd7e14',  // 上游警戒线 - 深橙色
        'WARNING_LINE_DOWN': '#fd7e14' // 下游警戒线 - 深橙色
      };
      
      return borderColorMap[areaType] || '#2c9d70'; // 默认深绿色
    },
    
    // 解析区域坐标
    parseAreaCoordinates(area) {
      try {
        if (area.coordinates && typeof area.coordinates === 'string') {
          // 尝试解析JSON字符串
          const coordinates = JSON.parse(area.coordinates);
          // 确保坐标是有效的二维数组
          if (Array.isArray(coordinates) && coordinates.length > 0) {
            return coordinates;
          }
        } else if (Array.isArray(area.coordinates)) {
          // 已经是数组格式
          return area.coordinates;
        }
      } catch (error) {
        console.error('解析区域坐标失败:', error);
      }
      
      // 默认返回空数组
      return [];
    },
    
    // 重写警戒区域图层显示/隐藏的方法
    toggleWarningAreaDisplay(forceCreate = false) {
      // 检查地图是否初始化
      if (!this.map) {
        console.warn('地图未初始化，无法操作警戒区域');
        return;
      }
      
      // 根据当前状态决定创建或销毁
      const shouldShowLayer = this.layerStatus.warningAreas;
      
      console.log(`警戒区域操作: ${shouldShowLayer ? '显示' : '隐藏'}, 强制创建:`, forceCreate);
      
      // 首先移除现有图层（如果存在）以避免重复
      if (this.warningAreasLayerGroup) {
        try {
          if (this.map.hasLayer(this.warningAreasLayerGroup)) {
            console.log('移除现有警戒区域图层');
            this.map.removeLayer(this.warningAreasLayerGroup);
          }
        } catch (e) {
          console.warn('移除现有警戒区域图层失败:', e);
        }
      }
      
      // 如果需要隐藏，直接销毁图层实例并返回
      if (!shouldShowLayer) {
        console.log('隐藏警戒区域图层');
        this.warningAreasLayerGroup = null;
        this.$message.info('已隐藏警戒区域');
        return;
      }
      
      // 如果需要显示，检查是否有区域数据
      if (!this.warningAreas || this.warningAreas.length === 0) {
        // 尝试从备份恢复
        if (this.areaDataBackup && this.areaDataBackup.length > 0) {
          console.log('从备份恢复警戒区域数据');
          this.warningAreas = JSON.parse(JSON.stringify(this.areaDataBackup));
          // 同时恢复polygonWarningAreas
          this.polygonWarningAreas = this.warningAreas.filter(area => area.geometryType !== 'POINT');
        } else if (!forceCreate) {
          // 如果没有数据且不是强制创建，则提示并返回
          console.warn('无法显示警戒区域：没有区域数据');
          this.$message.warning('当前没有警戒区域数据可显示');
          
          // 创建一个空的图层组，确保后续切换操作可以正常进行
          this.warningAreasLayerGroup = L.layerGroup();
          this.warningAreasLayerGroup._leaflet_id = 'emptyWarningAreasGroup_' + Date.now();
          this.warningAreasLayerGroup.name = 'warningAreasLayerGroup';
          this.warningAreasLayerGroup.isWarningAreaGroup = true;
          this.warningAreasLayerGroup.isEmpty = true;
          this.map.addLayer(this.warningAreasLayerGroup);
          
          return;
        } else {
          // 强制创建时，使用空数组
          console.log('初始化空的警戒区域数组');
          this.warningAreas = [];
          this.polygonWarningAreas = [];
        }
      }
      
      // 创建新图层
      console.log('创建警戒区域图层，区域数量:', this.polygonWarningAreas.length);
      if (typeof createWarningAreasLayer === 'function' && this.polygonWarningAreas.length > 0) {
        this.warningAreasLayerGroup = createWarningAreasLayer(
          this.map,
          this.polygonWarningAreas,
          (area) => {
            console.log('点击警戒区域:', area.id);
          }
        );
      } else {
        // 备用方法或创建空图层
        this.warningAreasLayerGroup = this.createBackupAreaLayer(false);
        
        // 标记为空图层（如果没有区域数据）
        if (!this.warningAreas || this.warningAreas.length === 0) {
          this.warningAreasLayerGroup.isEmpty = true;
        }
      }
      
      // 添加到地图并设置Z-index
      if (this.warningAreasLayerGroup) {
        this.warningAreasLayerGroup.setZIndex(50);
        this.map.addLayer(this.warningAreasLayerGroup);
        console.log('警戒区域图层已添加到地图，是否为空图层:', !!this.warningAreasLayerGroup.isEmpty);
        
        // // 避免为空图层显示成功消息
        // if (!this.warningAreasLayerGroup.isEmpty) {
        //   this.$message.success('已显示警戒区域');
        // }
        
        // 确保备份是最新的（如果有数据）
        if (this.warningAreas && this.warningAreas.length > 0) {
          this.areaDataBackup = JSON.parse(JSON.stringify(this.warningAreas));
        }
      } else {
        console.error('创建警戒区域图层失败');
        this.$message.error('显示警戒区域失败');
      }
    },
    
    // 优化备用创建警戒区域图层的方法
    createBackupAreaLayer(addToMap = false) {
      console.log('使用备用方法创建警戒区域图层');
      
      // 创建图层组
      const layerGroup = L.layerGroup();
      
      // 添加标识信息
      layerGroup._leaflet_id = 'backupAreaLayerGroup_' + Date.now();
      layerGroup.name = 'warningAreasLayerGroup'; // 保持名称一致
      layerGroup.isWarningAreaGroup = true;
      
      // 如果有区域数据，为每个区域创建多边形
      if (this.polygonWarningAreas && this.polygonWarningAreas.length > 0) {
        let createdCount = 0;
        
        this.polygonWarningAreas.forEach(area => {
          if (!area.points || !Array.isArray(area.points) || area.points.length < 3) {
            console.warn(`区域 ${area.id || '未知'} 点数不足，无法创建多边形`);
            return;
          }
          
          try {
            // 创建多边形
            const polygon = L.polygon(area.points, {
              color: area.borderColor || '#FF0000',
              weight: area.borderWidth || 2,
              fillColor: area.color || '#FF000080',
              fillOpacity: 0.2,
              pane: 'overlayPane' // 确保在正确的图层面板上
            });
            
            // 添加到图层组
            polygon.addTo(layerGroup);
            createdCount++;
            
            // 只在非绘制模式下显示区域名称标签
            // 检查是否有名称且不是临时绘制的区域
            if (area.name && !area.isDrawing && area.id) {
              const tooltipOptions = { 
                permanent: true, 
                direction: 'center', 
                className: 'area-label', 
                interactive: false 
              };
              polygon.bindTooltip(area.name, tooltipOptions);
            }
            
            // 添加点击事件
            polygon.on('click', (e) => {
              L.DomEvent.stopPropagation(e);
              console.log('点击区域:', area.id, area.name);
            });
          } catch (e) {
            console.error('创建区域失败:', e, area);
          }
        });
        
        console.log(`备用方法成功创建 ${createdCount}/${this.polygonWarningAreas.length} 个警戒区域`);
      } else {
        console.warn('备用方法创建空的警戒区域图层');
      }
      
      // 设置图层组Z-index
      layerGroup.setZIndex(50);
      
      // 如果需要，添加到地图
      if (addToMap && this.map) {
        this.map.addLayer(layerGroup);
        console.log('备用方法创建的警戒区域图层已添加到地图');
      }
      
      return layerGroup;
    },
    
    // 参照船舶图层实现的警戒区域图层可见性切换方法
    toggleWarningAreaVisibility(visible) {
      if (!this.map || !this.warningAreasLayerGroup) {
        console.warn('警戒区域图层未初始化，无法切换可见性');
        return;
      }
      
      console.log(`切换警戒区域可见性: ${visible ? '显示' : '隐藏'}`);
      
      try {
        if (visible) {
          // 显示警戒区域图层
          this.map.addLayer(this.warningAreasLayerGroup);
          this.warningAreasLayerGroup.setZIndex(50); // 确保在底层
          // this.$message.success('已显示警戒区域');
        } else {
          // 隐藏警戒区域图层
          this.map.removeLayer(this.warningAreasLayerGroup);
          this.$message.info('已隐藏警戒区域');
        }
      } catch (error) {
        console.error('切换警戒区域可见性失败:', error);
      }
    },
    
    // 参照船舶图层实现的航迹图层可见性切换方法
    toggleShipTracksVisibility(visible) {
      if (!this.map || !this.shipTracksLayerGroup) {
        console.warn('船舶航迹图层未初始化，无法切换可见性');
        return;
      }
      
      console.log(`切换航迹可见性: ${visible ? '显示' : '隐藏'}`);
      
      try {
        if (visible) {
          // 显示航迹图层
          this.map.addLayer(this.shipTracksLayerGroup);
          this.shipTracksLayerGroup.setZIndex(150); // 确保在区域之上
          this.$message.success('已显示船舶航迹');
        } else {
          // 隐藏航迹图层
          this.map.removeLayer(this.shipTracksLayerGroup);
          this.$message.info('已隐藏船舶航迹');
        }
      } catch (error) {
        console.error('切换船舶航迹可见性失败:', error);
      }
    },
    
    // 添加专门用于调试的方法
    debugWarningArea() {
      console.log('== 警戒区域图层调试信息 ==');
      console.log('状态信息:');
      console.log('- 当前图层显示状态:', this.layerStatus.warningAreas);
      console.log('- 警戒区域图层对象存在:', !!this.warningAreasLayerGroup);
      console.log('- 区域数据数量:', this.warningAreas?.length || 0);
      console.log('- 备份数据数量:', this.areaDataBackup?.length || 0);
      
      if (this.warningAreasLayerGroup && this.map) {
        console.log('- 图层是否在地图上:', this.map.hasLayer(this.warningAreasLayerGroup));
        console.log('- 图层中多边形数量:', 
          this.warningAreasLayerGroup.getLayers ? 
          this.warningAreasLayerGroup.getLayers().length : 
          '未知');
      }
      
      // 收集所有图层信息
      const allLayers = [];
      if (this.map) {
        this.map.eachLayer(layer => {
          if (layer instanceof L.Polygon || layer instanceof L.LayerGroup) {
            allLayers.push(layer);
          }
        });
        console.log('- 地图上所有多边形和图层组数量:', allLayers.length);
      }
      
      console.log('正在切换警戒区域状态...');
      // 切换状态
      this.layerStatus.warningAreas = !this.layerStatus.warningAreas;
      // 应用新方法
      this.toggleWarningAreaDisplay();
      
      return '警戒区域调试完成，请查看控制台输出';
    },
    
    // 添加重置警戒区域图层的方法
    resetWarningAreaLayer() {
      console.log('重置警戒区域图层, 当前区域数量:', this.warningAreas ? this.warningAreas.length : 0);
      
      // 确保warningAreas是数组
      if (!this.warningAreas) {
        this.warningAreas = [];
        this.polygonWarningAreas = [];
      }
      
      // 收集所有区域图层信息
      console.log('检查地图上的图层:');
      const polygonLayers = [];
      if (this.map) {
        this.map.eachLayer(layer => {
          if (layer instanceof L.Polygon) {
            polygonLayers.push(layer);
          }
        });
        console.log('- 地图上找到', polygonLayers.length, '个多边形图层');
      }
      
      // 移除现有图层
      if (this.warningAreasLayerGroup && this.map) {
        try {
          console.log('移除前检查图层是否在地图上:', this.map.hasLayer(this.warningAreasLayerGroup));
          this.map.removeLayer(this.warningAreasLayerGroup);
          console.log('移除后检查图层是否在地图上:', this.map.hasLayer(this.warningAreasLayerGroup));
          console.log('成功移除现有警戒区域图层');
          
          // 额外清理
          if (this.warningAreasLayerGroup.clearLayers) {
            this.warningAreasLayerGroup.clearLayers();
          }
          
          // 移除所有找到的多边形
          polygonLayers.forEach(layer => {
            try {
              this.map.removeLayer(layer);
            } catch (e) {
              console.warn('移除多边形失败', e);
            }
          });
        } catch (e) {
          console.warn('移除警戒区域图层出错', e);
        }
      }
      
      // 销毁旧图层引用
      this.warningAreasLayerGroup = null;
      
      // 从备份恢复数据
      if (this.areaDataBackup && this.areaDataBackup.length > 0) {
        this.warningAreas = JSON.parse(JSON.stringify(this.areaDataBackup));
        // 同时恢复polygonWarningAreas
        this.polygonWarningAreas = this.warningAreas.filter(area => area.geometryType !== 'POINT');
        console.log('数据已从备份恢复，数量:', this.warningAreas.length);
      }
      
      // 重置状态
      this.layerStatus.warningAreas = true;
      
      // 创建新图层
      if (this.warningAreas.length > 0) {
        console.log('使用已有区域数据创建新图层');
        this.warningAreasLayerGroup = this.createWarningAreasLayer();
        
        if (this.layerStatus.warningAreas && this.warningAreasLayerGroup) {
          this.map.addLayer(this.warningAreasLayerGroup);
          console.log('新警戒区域图层已添加到地图');
          console.log('检查图层是否在地图上:', this.map.hasLayer(this.warningAreasLayerGroup));
        }
      } else {
        console.log('没有区域数据，创建空图层');
        this.warningAreasLayerGroup = L.layerGroup();
      }
      
      return '警戒区域图层已重置';
    },

    // 修改地图初始化完成后的处理 - 在mounted或地图初始化回调中添加此方法
    initMapLayers() {
      console.log('初始化地图图层...');
      // 确保仅在地图初始化后调用此方法
      if (!this.map) {
        console.warn('地图尚未初始化，无法创建图层');
        return;
      }
      
      // 初始化基础图层（船舶和航迹）
      this.initBaseMapLayers();
      
      // 处理警戒区域图层初始化
      if (this.layerStatus.warningAreas) {
        console.log('通过统一方法初始化警戒区域图层...');
        this.initWarningAreaLayer();
      }
      
      // 初始化雷达目标图层
      if (this.layerStatus.radarTarget) {
        console.log('初始化雷达目标图层...');
        this.initRadarTargetLayer();
      }
    },

    // 新增初始化基础图层的方法（不包括警戒区域）
    initBaseMapLayers() {
      console.log('初始化基础图层...');
      // 确保仅在地图初始化后调用此方法
      if (!this.map) {
        console.warn('地图尚未初始化，无法创建基础图层');
        return;
      }
      
      // 初始化船舶图层
      this.createShipIcon();
      this.initCanvasLayer();
      this.loadShipData();
      
      // 初始化航迹图层
      this.createShipTracksLayer();
      
      // 初始化雷达图标
      this.createRadarIcon();
    },
    
    // 新增方法：初始化警戒区域图层
    initWarningAreaLayer(forceCreate = false) {
      console.log('初始化警戒区域图层，强制创建:', forceCreate);
      
      if (!this.map) {
        console.warn('地图尚未初始化，无法创建警戒区域图层');
        return;
      }
      
      // 如果强制创建或状态为显示，则调用切换方法
      if (forceCreate || this.layerStatus.warningAreas) {
        console.log('初始化时创建警戒区域图层');
        // 确保图层状态为显示
        this.layerStatus.warningAreas = true;
        // 调用统一的切换方法，并传入forceCreate参数
        this.toggleWarningAreaDisplay(forceCreate);
      }
    },

    // 完善地图初始化完成的回调方法
    onMapReady(mapInstance) {
      this.map = mapInstance;
      
      // 初始化地图功能
      this.initLayers();
      
      // 先创建船舶图标，确保图标已准备好
      this.createShipIcon();
      
      // 添加船舶图标事件监听
      this.addShipEventListeners();
      
      // 使用Promise和延迟确保地图完全初始化
      setTimeout(() => {
        try {
          console.log('开始初始化地图图层...');
          
          // 1.5 初始化文本标注防重叠管理器（在任何标注创建前绑定地图）
          if (!this.mapLabelManager) {
            this.mapLabelManager = createMapLabelManager(this.map, {
              collisionSelector: '.area-label',
              drawReferenceLine: true
            });
          }

          // 2. 警戒区域图层由 handleBridgesSelectionChange / loadBridgeAreas 流程统一创建，
          //    此处不再提前清除图层或创建空图层，原因：
          //    - forceRemoveAllWarningAreaLayers() 会清空 warningAreas 数据，导致后续区域数据加载结果丢失
          //    - 提前占位 this.warningAreasLayerGroup 会使后续切换逻辑重复创建 LayerGroup（文本标注重复绑定）
          console.log('警戒区域图层由桥梁选择流程统一创建');
          
          // 3. 然后加载和创建船舶航迹图层（中间层）
          console.log('开始创建船舶航迹图层...');
          this.createShipTracksLayer();
          
          // 确保船舶航迹图层被添加到地图上
          if (this.shipTracksLayerGroup && this.layerStatus.shipTracks) {
            // 如果航迹图层已创建但未添加到地图，则添加
            if (!this.map.hasLayer(this.shipTracksLayerGroup)) {
              this.map.addLayer(this.shipTracksLayerGroup);
              this.shipTracksLayerGroup.setZIndex(150); // 设置层级
              console.log('船舶航迹图层已添加到地图并设置层级');
            }
          }
          
          // 4. 创建雷达目标图层
          console.log('开始创建雷达目标图层...');
          this.createRadarIcon();
          this.initRadarTargetLayer();
          
          // 5. 最后初始化船舶Canvas图层（顶层）
          console.log('开始初始化船舶Canvas图层...');
          this.initCanvasLayer();
          
          // 6. 明确设置地图就绪状态
          console.log('设置地图就绪状态为true');
          this.mapReady = true;
          this.mapInitialized = true;
          
          // 7. 初始化AIS和雷达数据接收器 - 确保顺序执行
          this.initDataReceiversWithOrder();
          
          // 8. 统一更新图层可见性
          console.log('更新所有图层可见性...');
          this.updateLayerVisibility();
          
          console.log('地图图层初始化完成');
        } catch (error) {
          console.error('地图图层初始化过程中出错:', error);
        }
      }, 800); // 增加延迟时间，确保地图完全加载
      
      // 触发事件通知地图已准备就绪
      this.$emit('map-ready', mapInstance);
    },
    
    // 添加船舶图标事件监听
    addShipEventListeners() {
      if (!this.map) return;
      
      console.log('添加船舶图标事件监听');
      
      // 监听船舶点击事件
      this.map.on('ship-click', (e) => {
        console.log('接收到船舶点击事件:', e.ship.userid);
        if (e.ship) {
          this.showShipInfo(e.ship);
        }
      });
      
      // 监听船舶鼠标悬停事件
      this.map.on('ship-mouseover', (e) => {
        if (e.ship) {
          // 在这里可以添加鼠标悬停效果，例如改变鼠标样式
          this.map._container.style.cursor = 'pointer';
          
          // 触发Vue组件事件
          this.$emit('ship-mouseover', e.ship);
        }
      });
      
      // 监听船舶鼠标离开事件
      this.map.on('ship-mouseout', (e) => {
        // 恢复默认鼠标样式
        this.map._container.style.cursor = '';
        
        // 触发Vue组件事件
        if (e.ship) {
          this.$emit('ship-mouseout', e.ship);
        }
      });
      
      // 监听船舶位置变更事件
      this.map.on('ship-position-changed', (e) => {
        // 如果变更的船舶是当前高亮的船舶，则更新高亮标记位置
        if (this.currentHighlightShip && e.ship && 
            this.currentHighlightShip.userid === e.ship.userid && 
            this.highlightMarker) {
          
          // 更新高亮标记位置
          this.highlightMarker.setLatLng([e.newPosition.lat, e.newPosition.lng]);
          
          // 更新当前高亮船舶引用
          this.currentHighlightShip = e.ship;
        }
      });
    },
    
    // 移除船舶图标事件监听
    removeShipEventListeners() {
      if (!this.map) return;
      
      console.log('移除船舶图标事件监听');
      
      // 移除所有自定义事件监听
      this.map.off('ship-click');
      this.map.off('ship-mouseover');
      this.map.off('ship-mouseout');
    },

    // 按顺序初始化数据接收器的新方法
    async initDataReceiversWithOrder() {
      try {
        console.log('开始按顺序初始化数据接收器...');
        
        // 检查是否已经初始化过
        const aisInitialized = this.aisDataReceiver && this.aisDataReceiver.isConnected;
        const radarInitialized = this.radarDataReceiver && this.radarDataReceiver.isConnected;
        
        if (aisInitialized && radarInitialized) {
          console.log('AIS和雷达数据接收器已初始化，直接通知地图就绪');
          this.notifyDataReceiversMapReady();
          return;
        }
        
        // 1. 先初始化AIS数据接收器 - 它有更高优先级
        console.log('开始初始化AIS数据接收器...');
        await this.initAisDataReceiver();
        
        // 等待一段时间，确保AIS接收器有足够时间建立连接
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // 2. 然后初始化雷达数据接收器
        console.log('开始初始化雷达数据接收器...');
        await this.initRadarDataReceiver();
        
        // 等待一段时间，确保两个接收器都有足够时间完成初始化
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // 3. 通知数据接收器地图已就绪
        console.log('通知数据接收器地图已就绪...');
        this.notifyDataReceiversMapReady();
        
        // 4. 延迟检查接收器状态
        setTimeout(() => {
          console.log('数据接收器状态检查:');
          if (this.aisDataReceiver) {
            console.log('- AIS接收器已连接:', this.aisDataReceiver.isConnected);
            console.log('- AIS接收器已订阅:', this.aisDataReceiver.isSubscribed);
            console.log('- AIS船舶数量:', this.aisDataReceiver.shipCount);
          }
          if (this.radarDataReceiver) {
            console.log('- 雷达接收器已连接:', this.radarDataReceiver.isConnected);
            console.log('- 雷达接收器已订阅:', this.radarDataReceiver.isSubscribed);
            console.log('- 雷达目标数量:', this.radarDataReceiver.targetCount || 0);
          }
        }, 2000);
      } catch (error) {
        console.error('初始化数据接收器过程中出错:', error);
      }
    },

    // 修改initAisDataReceiver返回Promise以便控制顺序
    async initAisDataReceiver() {
      console.log('初始化AIS数据接收器');
      
      try {
        // 确保使用全局导入的aisDataReceiver实例
        this.aisDataReceiver = aisDataReceiver;
        
        if (!this.aisDataReceiver) {
          console.error('AIS数据接收器实例不可用，请检查导入');
          return Promise.reject(new Error('AIS数据接收器实例不可用'));
        }
        
        // 初始化AIS数据接收器
        this.aisDataReceiver.init(this, {
          autoReconnect: true,
          maxReconnectAttempts: 999999,
          reconnectInterval: 3
        });
        
        // 先移除可能存在的旧事件监听，避免重复绑定
        this.removeAisEventListeners();
        
        // 添加事件监听
        this.addAisEventListeners();
        
        // 连接并开始接收数据
        console.log('开始连接AIS数据接收器...');
        return this.aisDataReceiver.connect()
          .then(() => {
            console.log('AIS数据接收器连接成功');
            
            // 连接成功后，如果地图已就绪，立即通知接收器
            if (this.mapReady && typeof this.aisDataReceiver.setMapReady === 'function') {
              console.log('立即通知AIS数据接收器地图已就绪');
              this.aisDataReceiver.setMapReady(true);
            }
            
            return true;
          })
          .catch(error => {
            console.error('AIS数据接收器连接失败:', error);
            return Promise.reject(error);
          });
      } catch (error) {
        console.error('初始化AIS数据接收器时出错:', error);
        return Promise.reject(error);
      }
    },

    // 修改initRadarDataReceiver返回Promise以便控制顺序
    async initRadarDataReceiver() {
      console.log('初始化雷达数据接收器');
      
      // 检查雷达接收器是否可用
      if (!radarDataReceiver) {
        console.error('雷达数据接收器未定义，请检查导入');
        return Promise.reject(new Error('雷达数据接收器未定义'));
      }
      
      try {
        // 保存到组件实例
        this.radarDataReceiver = radarDataReceiver;
        
        // 初始化雷达数据接收器
        this.radarDataReceiver.init(this, {
          autoReconnect: true,
          maxReconnectAttempts: 999999,
          reconnectInterval: 3
        });
        
        // 添加事件监听
        this.addRadarEventListeners();
        
        // 连接并开始接收数据
        console.log('开始连接雷达数据接收器...');
        return this.radarDataReceiver.connect()
          .then(() => {
            console.log('雷达数据接收器连接成功');
            
            // 如果地图已就绪，立即通知接收器
            if (this.mapReady && this.radarDataReceiver && typeof this.radarDataReceiver.setMapReady === 'function') {
              console.log('立即通知雷达数据接收器地图已就绪');
              this.radarDataReceiver.setMapReady(true);
            }
            
            return true;
          })
          .catch(error => {
            console.error('雷达数据接收器连接失败:', error);
            return Promise.reject(error);
          });
      } catch (error) {
        console.error('初始化雷达数据接收器时出错:', error);
        return Promise.reject(error);
      }
    },

    // 修改notifyDataReceiversMapReady方法
    notifyDataReceiversMapReady() {
      console.log('通知数据接收器地图已就绪');
      
      // 确保地图就绪状态为true
      this.mapReady = true;
      
      // 通知AIS数据接收器
      if (this.aisDataReceiver) {
        try {
          if (typeof this.aisDataReceiver.setMapReady === 'function') {
            console.log('正在通知AIS数据接收器地图就绪状态...');
            this.aisDataReceiver.setMapReady(true);
            console.log('已通知AIS数据接收器地图就绪');
          } else {
            console.warn('AIS数据接收器缺少setMapReady方法');
          }
        } catch (e) {
          console.warn('通知AIS数据接收器地图就绪时出错:', e);
        }
      } else {
        console.warn('AIS数据接收器未初始化，无法通知地图就绪状态');
      }
      
      // 通知雷达数据接收器
      if (this.radarDataReceiver) {
        try {
          if (typeof this.radarDataReceiver.setMapReady === 'function') {
            console.log('正在通知雷达数据接收器地图就绪状态...');
            this.radarDataReceiver.setMapReady(true);
            console.log('已通知雷达数据接收器地图就绪');
          } else {
            console.warn('雷达数据接收器缺少setMapReady方法');
          }
        } catch (e) {
          console.warn('通知雷达数据接收器地图就绪时出错:', e);
        }
      } else {
        console.warn('雷达数据接收器未初始化，无法通知地图就绪状态');
      }
      
      // 延迟一段时间后检查接收器状态
      setTimeout(() => {
        console.log('数据接收器状态检查:');
        if (this.aisDataReceiver) {
          console.log('- AIS接收器已连接:', this.aisDataReceiver.isConnected);
          console.log('- AIS接收器已订阅:', this.aisDataReceiver.isSubscribed);
          console.log('- AIS船舶数量:', this.aisDataReceiver.shipCount);
        }
        if (this.radarDataReceiver) {
          console.log('- 雷达接收器已连接:', this.radarDataReceiver.isConnected);
          console.log('- 雷达接收器已订阅:', this.radarDataReceiver.isSubscribed);
          console.log('- 雷达目标数量:', this.radarDataReceiver.targetCount || 0);
        }
      }, 2000);
    },

    // 添加AIS事件监听
    addAisEventListeners() {
      if (!aisEvents) {
        console.error('AIS事件对象不可用，请检查导入');
        return;
      }
      
      console.log('添加AIS事件监听');
      
      // 使用bind确保回调函数中的this指向正确的组件实例
      this._boundShipDataUpdated = this.handleAisShipDataUpdated.bind(this);
      this._boundConnectionStatusChanged = this.handleAisConnectionStatusChanged.bind(this);
      
      // 监听船舶数据更新
      aisEvents.onShipDataUpdated(this._boundShipDataUpdated);
      
      // 监听连接状态变更
      aisEvents.onConnectionStatusChanged(this._boundConnectionStatusChanged);
    },
    
    // 移除AIS事件监听
    removeAisEventListeners() {
      if (!aisEvents) return;
      
      console.log('移除AIS事件监听');
      
      // 使用保存的绑定函数移除事件监听
      if (this._boundShipDataUpdated) {
        aisEvents.offShipDataUpdated(this._boundShipDataUpdated);
      }
      
      if (this._boundConnectionStatusChanged) {
        aisEvents.offConnectionStatusChanged(this._boundConnectionStatusChanged);
      }
    },
    
    // 优化AIS数据处理方法
    handleAisShipDataUpdated(data) {
      try {
        // 检查数据有效性
        if (!data || !data.ships || !Array.isArray(data.ships)) {
          console.warn('接收到无效的船舶数据');
          return;
        }
        
        // 输出详细日志，帮助调试
        // console.log(`接收到AIS船舶数据更新，数量: ${data.ships.length}，第一条数据:`, 
        //   data.ships.length > 0 ? JSON.stringify(data.ships[0]) : '无');
        
        // 检查是否已有数据
        const isFirstUpdate = this.ships.length === 0 && data.ships.length > 0;
        
        // 保存当前高亮船舶的MMSI（如果有）
        const currentHighlightMMSI = this.currentHighlightShip ? this.currentHighlightShip.userid : null;
        
        // 检测船舶位置变更
        if (!isFirstUpdate && this.map) {
          // 创建船舶位置变更事件列表
          const positionChanges = [];
          
          // 检查每艘船舶是否位置发生变化
          data.ships.forEach(newShip => {
            if (!newShip || !newShip.pos || !newShip.userid) return;
            
            // 查找旧数据中的对应船舶
            const oldShip = this.ships.find(s => s.userid === newShip.userid);
            
            // 如果找到旧数据且位置不同，则记录位置变更
            if (oldShip && oldShip.pos && 
                (oldShip.pos.latitude !== newShip.pos.latitude || 
                 oldShip.pos.longitude !== newShip.pos.longitude)) {
              
              positionChanges.push({
                ship: newShip,
                oldPosition: {
                  lat: oldShip.pos.latitude,
                  lng: oldShip.pos.longitude
                },
                newPosition: {
                  lat: newShip.pos.latitude,
                  lng: newShip.pos.longitude
                }
              });
            }
          });
          
          // 如果有位置变更，触发事件
          if (positionChanges.length > 0) {
            // 触发全局位置变更事件
            this.map.fire('ships-position-changed', {
              changes: positionChanges
            });
            
            // 对每艘船舶触发单独的位置变更事件
            positionChanges.forEach(change => {
              this.map.fire('ship-position-changed', {
                ship: change.ship,
                oldPosition: change.oldPosition,
                newPosition: change.newPosition
              });
              
              // 触发Vue组件事件
              this.$emit('ship-position-changed', {
                ship: change.ship,
                oldPosition: change.oldPosition,
                newPosition: change.newPosition
              });
            });
          }
        }
        
        // 更新组件内船舶数据 - 无论地图是否就绪都更新数据
        this.ships = data.ships;
        
        // 如果有当前显示的船舶面板，同步更新currentShip的AIS实时数据
        if (this.currentShip && this.currentShip.userid && this.shipPanelVisible) {
          const updatedCurrentShip = this.ships.find(ship => ship.userid === this.currentShip.userid);
          if (updatedCurrentShip) {
            // 只更新AIS实时数据，保留API获取的详细信息
            this.currentShip = {
              ...this.currentShip, // 保留现有的详细信息（API获取的）
              // 更新AIS实时数据
              pos: updatedCurrentShip.pos,
              cog: updatedCurrentShip.cog,
              sog: updatedCurrentShip.sog,
              heading: updatedCurrentShip.heading,
              receive_time: updatedCurrentShip.receive_time,
              // 如果基础信息在实时数据中有更新，也同步更新
              shipname: updatedCurrentShip.shipname || this.currentShip.shipname,
              cnName: updatedCurrentShip.cnName || this.currentShip.cnName
            };
            // console.log('已同步更新当前船舶AIS数据:', this.currentShip.userid, this.currentShip.receive_time);
          }
        }
        
        // 如果有高亮的船舶，检查其位置是否更新，如果更新了则更新高亮标记
        if (currentHighlightMMSI && this.map && this.highlightMarker) {
          const updatedShip = this.ships.find(ship => ship.userid === currentHighlightMMSI);
          if (updatedShip && updatedShip.pos) {
            // 更新当前高亮的船舶引用
            this.currentHighlightShip = updatedShip;
            
            // 更新高亮标记的位置
            const newPosition = [updatedShip.pos.latitude, updatedShip.pos.longitude];
            this.highlightMarker.setLatLng(newPosition);
            // console.log('已更新高亮船舶的位置:', newPosition);
            
            // 触发高亮船舶位置更新事件
            this.map.fire('highlight-ship-position-changed', {
              ship: updatedShip,
              position: newPosition
            });
          }
        }
        
        // 如果正在查看航迹，跳过船舶更新
        if (this.showTrackViewer) {
          console.log('正在查看航迹，跳过船舶标记更新');
          return;
        }
        
        // 只有当显示船舶图层时才更新船舶标记
        if (!this.layerStatus.ship) {
          console.log('船舶图层未启用，跳过更新');
          return;
        }
        
        // 如果地图未初始化，跳过更新
        if (!this.map) {
          console.log('地图未初始化，跳过更新');
          return;
        }
        
        // 如果Canvas图层未初始化，尝试初始化
        if (!this.layerInitStatus.canvasInitialized || !this.canvasLayer) {
          // 避免重复的初始化尝试
          if (!this._canvasInitAttempt) {
            console.log('Canvas图层未初始化，尝试初始化');
            this._canvasInitAttempt = true;
            this.initCanvasLayer();
            
            // 重置尝试标志，允许将来再次尝试
            setTimeout(() => {
              this._canvasInitAttempt = false;
            }, 2000);
          } else {
            console.log('Canvas图层初始化尝试进行中，跳过本次更新');
            return;
          }
        }
        
        // 确保Canvas图层已就绪且船舶图层是启用状态
        if (this.layerInitStatus.canvasInitialized && this.canvasLayer && this.layerStatus.ship) {
          // 使用防抖方式更新船舶标记
          if (this._shipUpdateTimeout) {
            clearTimeout(this._shipUpdateTimeout);
          }
          
          this._shipUpdateTimeout = setTimeout(() => {
            // console.log('更新船舶标记，数量:', this.ships.length);
            this.updateShipMarkers();
            this._shipUpdateTimeout = null;
          }, 500);
          
          // 如果是第一次接收数据，且有船舶数据，可以将地图居中到船舶位置
          if (isFirstUpdate && this.ships.length > 0) {
            // 找到附近船舶的平均位置
            this.updateMapCenter();
          }
        } else {
          console.log('Canvas图层未就绪或船舶图层未启用，无法更新船舶标记');
        }
      } catch (error) {
        console.error('处理AIS船舶数据更新时出错:', error);
      }
    },
    
    // 处理AIS连接状态变更
    handleAisConnectionStatusChanged(statusData) {
      console.log('AIS连接状态变更:', statusData.isConnected ? '已连接' : '已断开');
      
      // 可以在这里处理连接状态变化的UI反馈
      if (statusData.isConnected) {
        // 连接成功的处理
      } else {
        // 连接断开的处理
      }
    },
    
    // 加载船舶数据 - 使用AisDataReceiver
    loadShipData() {
      console.log('加载实时船舶数据');
      
      // 如果AIS数据接收器已初始化，直接使用
      if (this.aisDataReceiver && this.aisDataReceiver.isConnected) {
        console.log('AIS数据接收器已连接，继续使用现有连接');
        return;
      }
      
      // 否则重新初始化
      this.initAisDataReceiver();
    },
    
    // 更新地图中心点
    updateMapCenter() {
      if (!this.ships || this.ships.length === 0) return;
      
      let sumLon = 0;
      let sumLat = 0;
      let count = 0;
      
      this.ships.forEach(ship => {
        if (ship.pos && ship.pos.longitude && ship.pos.latitude) {
          sumLon += parseFloat(ship.pos.longitude);
          sumLat += parseFloat(ship.pos.latitude);
          count++;
        }
      });
      
      if (count > 0) {
        this.center = [sumLat / count, sumLon / count];
      }
    },
    
    // 从localStorage获取保存的桥梁信息并设置初始地图中心点
    initMapCenterFromBridge() {
      try {
        console.log('从localStorage获取桥梁信息并设置地图中心点');
        const savedBridge = localStorage.getItem('selectedBridge');
        
        if (!savedBridge) {
          console.warn('localStorage中未找到已保存的桥梁信息');
          return;
        }
        
        const bridgeData = JSON.parse(savedBridge);
        console.log('从localStorage获取的桥梁信息:', bridgeData);
        
        if (!bridgeData) {
          console.warn('解析localStorage中的桥梁数据失败');
          return;
        }
          
        // 检查桥梁数据是否包含ID
        if (!bridgeData.id) {
          console.warn('localStorage中的桥梁数据不包含ID');
          return;
        }
          
        // 检查桥梁数据是否包含位置信息
        if (bridgeData.latitude && bridgeData.longitude) {
          // 直接使用localStorage中的位置信息设置地图中心点
          const lat = parseFloat(bridgeData.latitude);
          const lng = parseFloat(bridgeData.longitude);
          
          if (!isNaN(lat) && !isNaN(lng)) {
            console.log(`使用localStorage缓存的桥梁位置: [${lat}, ${lng}]`);
            this.center = [lat, lng];
            
            // 设置适当的缩放级别
            this.zoom = 15;
            
            // 如果地图已初始化，直接设置视图
            if (this.map) {
              this.map.setView([lat, lng], this.zoom);
            }
            
            // 检查是否存在多桥梁选择状态
            const multiBridgeState = localStorage.getItem('multiBridgeSelection');
            if (multiBridgeState) {
              try {
                const state = JSON.parse(multiBridgeState);
                if (state.selectedBridgeIds && state.selectedBridgeIds.length > 1) {
                  console.log('检测到多桥梁状态，跳过单桥梁区域加载，等待多桥梁状态恢复');
                  return;
                }
              } catch (e) {
                console.warn('解析多桥梁状态失败:', e);
              }
            }
            
            // 单桥梁状态下才加载区域数据
            this.loadBridgeAreas(bridgeData.id);
            return;
          }
        }
        
        // 如果没有位置信息，从服务器获取详情
        console.log('localStorage中缓存的桥梁数据不包含位置信息，从服务器获取');
        this.loadBridgeDetails(bridgeData.id);
      } catch (error) {
        console.error('从localStorage获取桥梁信息失败:', error);
      }
    },

    // 处理雷达目标数据更新
    handleRadarDataUpdated(data) {
      try {
        // 检查数据有效性
        if (!data || !data.targets || !Array.isArray(data.targets)) {
          console.warn('接收到无效的雷达目标数据');
          return;
        }
        
        // 更新组件内雷达目标数据
        this.radarTargets = data.targets;
        
        // 如果地图未初始化，跳过更新
        if (!this.map) {
          console.log('地图未初始化，跳过更新');
          return;
        }
        
        // 如果雷达目标图层未初始化，尝试初始化
        if (!this.radarLayerInitialized || !this.radarTargetLayer) {
          // 避免重复的初始化尝试
          if (!this._radarLayerInitAttempt) {
            console.log('雷达目标图层未初始化，尝试初始化');
            this._radarLayerInitAttempt = true;
            this.initRadarTargetLayer();
            
            // 重置尝试标志，允许将来再次尝试
            setTimeout(() => {
              this._radarLayerInitAttempt = false;
            }, 2000);
          } else {
            console.log('雷达目标图层初始化尝试进行中，跳过本次更新');
            return;
          }
        }
        
        // 确保雷达目标图层已就绪
        if (this.radarLayerInitialized && this.radarTargetLayer) {
          // 使用防抖方式更新雷达目标标记
          if (this._radarUpdateTimeout) {
            clearTimeout(this._radarUpdateTimeout);
          }
          
          this._radarUpdateTimeout = setTimeout(() => {
            // console.log('更新雷达目标标记，数量:', this.radarTargets.length);
            // 只有当雷达图层可见时才更新标记
            if (this.layerStatus.radarTarget) {
              this.updateRadarTargetMarkers();
            }
            this._radarUpdateTimeout = null;
          }, 500);
        } else {
          console.log('雷达目标图层未就绪，无法更新雷达目标标记');
        }
      } catch (error) {
        console.error('处理雷达目标数据更新时出错:', error);
      }
    },
    
    // 添加雷达事件监听
    addRadarEventListeners() {
      if (!radarEvents) {
        console.error('雷达事件对象不可用，请检查导入');
        return;
      }
      
      console.log('添加雷达事件监听');
      
      // 使用bind确保回调函数中的this指向正确的组件实例
      this._boundRadarDataUpdated = this.handleRadarDataUpdated.bind(this);
      this._boundRadarConnectionStatusChanged = this.handleRadarConnectionStatusChanged.bind(this);
      
      // 监听雷达数据更新
      radarEvents.onRadarDataUpdated(this._boundRadarDataUpdated);
      
      // 监听连接状态变更
      radarEvents.onConnectionStatusChanged(this._boundRadarConnectionStatusChanged);
    },
    
    // 移除雷达事件监听
    removeRadarEventListeners() {
      if (!radarEvents) return;
      
      console.log('移除雷达事件监听');
      
      // 使用保存的绑定函数移除事件监听
      if (this._boundRadarDataUpdated) {
        radarEvents.offRadarDataUpdated(this._boundRadarDataUpdated);
      }
      
      if (this._boundRadarConnectionStatusChanged) {
        radarEvents.offConnectionStatusChanged(this._boundRadarConnectionStatusChanged);
      }
    },
    
    // 处理雷达连接状态变更
    handleRadarConnectionStatusChanged(statusData) {
      console.log('雷达连接状态变更:', statusData.isConnected ? '已连接' : '已断开');
      
      // 可以在这里处理连接状态变化的UI反馈
      if (statusData.isConnected) {
        // 连接成功的处理
      } else {
        // 连接断开的处理
      }
    },
    
    // 打开船舶信息对话框 - 保留此方法以兼容可能的现有调用
    openShipInfoDialog(ship) {
      this.showShipInfo(ship);
    },
    
    // 格式化日期时间为yyyy-MM-dd HH:mm:ss格式
    formatDateTime(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    
    // 显示船舶航迹详情
    showShipTrackDetail() {
      if (!this.currentShip || !this.currentShip.userid) {
        this.$message.warning('当前船舶MMSI不可用，无法查询航迹');
        return;
      }
      
      // 保存当前选中的船舶MMSI和名称
      const newMmsi = String(this.currentShip.userid);
      const shipName = this.currentShip.shipname || this.currentShip.userid;
      
      console.log('查看航迹详情，当前船舶:', {
        mmsi: newMmsi,
        name: shipName,
        position: this.currentShip.pos ? [this.currentShip.pos.latitude, this.currentShip.pos.longitude] : '未知'
      });
      
      // 确保时间范围有效
      if (!this.trackTimeRange || !Array.isArray(this.trackTimeRange) || this.trackTimeRange.length !== 2) {
        // 预设航迹时间范围为最近24小时，取最后10条数据
        const endTime = new Date();
        const startTime = new Date(endTime.getTime() - 24 * 60 * 60 * 1000); // 24小时前
        
        this.trackTimeRange = [
          this.formatDateTime(startTime),
          this.formatDateTime(endTime)
        ];
        
        // 设置默认查询模式
        this.trackQueryMode = {
          useLatestRecords: true,
          maxRecords: 10
        };
        
        console.log('重新设置时间范围（24小时，最后10条）:', this.trackTimeRange);
      }
      
      // 输出MMSI和时间范围
      console.log('当前船舶MMSI:', newMmsi);
      console.log('查询时间范围:', this.trackTimeRange);
      
      // 不关闭船舶信息面板，保持面板显示
      // this.shipPanelVisible = true;
      
      // 如果已经显示了查看器，并且是同一个船舶，则不需要重新打开
      // 直接使用this.currentShipMmsi变量，而不是依赖已移除的计算属性
      if (this.showTrackViewer && this.currentShipMmsi === newMmsi) {
        console.log(`已经在查看船舶 ${shipName} 的航迹数据，避免重复打开`);
        return;
      }
      
      // 设置当前船舶MMSI - 会触发组件重新渲染，同时watch会触发API调用
      this.currentShipMmsi = newMmsi;
      
      // 保存当前船舶图层状态并隐藏船舶图层
      if (this.shipLayerStateBeforeTrack === null) {
        this.shipLayerStateBeforeTrack = this.layerStatus.ship;
        console.log('保存显示航迹前的船舶图层状态:', this.shipLayerStateBeforeTrack);
        
        // 如果船舶图层当前是显示状态，则隐藏它
        if (this.layerStatus.ship) {
          this.layerStatus.ship = false;
          this.toggleShipVisibility(false);
          console.log('隐藏船舶图层以显示航迹');
        }
      }
      
      // 显示航迹查看器
      this.showTrackViewer = true;
      
      // 记录要显示航迹的船舶
      console.log(`显示船舶 ${shipName} 的航迹数据`);
    },
    onFullscreenChange(isFullscreen) {
      // 通知父组件全屏状态改变
      this.$emit('fullscreen-change', isFullscreen);
      console.log('全屏状态变更:', isFullscreen ? '已进入全屏' : '已退出全屏');
    },
    closeTrackViewer() {
      console.log('关闭航迹查看器');
      
      // 恢复显示航迹前的船舶图层状态
      if (this.shipLayerStateBeforeTrack !== null) {
        console.log('恢复显示航迹前的船舶图层状态:', this.shipLayerStateBeforeTrack);
        this.layerStatus.ship = this.shipLayerStateBeforeTrack;
        this.toggleShipVisibility(this.shipLayerStateBeforeTrack);
        this.shipLayerStateBeforeTrack = null;
        console.log('船舶图层状态已恢复');
      }
      
      // 先将显示标志设为false
      this.showTrackViewer = false;
      
      // 重置航迹相关数据
      setTimeout(() => {
        if (!this.showTrackViewer) {
          console.log('重置航迹查看器状态');
          
          // 重置currentShipMmsi，确保下次显示时重新加载
          this.currentShipMmsi = null;
          
          try {
            // 如果船舶图层处于启用状态，完全重新初始化Canvas图层
            if (this.layerStatus.ship) {
              console.log('重新初始化船舶Canvas图层');
              
              // 确保有正确的船舶图标
              if (!this.shipIcon) {
                this.createShipIcon();
              }
              
              // 强制重新初始化Canvas图层
              this.initCanvasLayer();
              
              // 触发地图刷新
              if (this.map) {
                this.map.invalidateSize();
              }
            }
          } catch (error) {
            console.error('关闭航迹查看器时重置Canvas图层出错:', error);
          }
        }
      }, 300);
    },
    closeShipDialog() {
      this.closeShipPanel();
    },

    // 添加完整的图层清理方法
    cleanupLayers() {
      // 清理Canvas图层
      this.cleanupCanvasLayer();
      
      // 清理航迹图层
      if (this.shipTracksLayerGroup && this.map) {
        try {
          this.map.removeLayer(this.shipTracksLayerGroup);
        } catch(e) {
          console.warn('移除航迹图层失败:', e);
        }
        this.shipTracksLayerGroup = null;
        this.layerInitStatus.tracksInitialized = false;
      }
      
      // 清理警戒区域图层
      if (this.warningAreasLayerGroup && this.map) {
        try {
          this.map.removeLayer(this.warningAreasLayerGroup);
        } catch(e) {
          console.warn('移除警戒区域图层失败:', e);
        }
        this.warningAreasLayerGroup = null;
        this.layerInitStatus.warningAreasInitialized = false;
      }
    },
    
    // 雷达图层相关方法
    createRadarIcon() {
      // 创建雷达图标
      if (createRadarIcon) {
        this.radarIcon = createRadarIcon(require('@/assets/ship-radar.png'));
      } else {
        console.log('创建雷达图标');
      }
    },
    
    initRadarTargetLayer() {
      // 如果图层已经创建，不再重复初始化
      if (this.radarLayerInitialized && this.radarTargetLayer) {
        console.log('雷达目标图层已经初始化，无需重复创建');
        return;
      }
      
      // 如果地图未初始化，直接退出
      if (!this.map) {
        console.log('地图未初始化，不创建雷达目标图层');
        return;
      }
      
      console.log('开始初始化雷达目标图层');
      
      // 创建雷达图标（如果尚未创建）
      if (!this.radarIcon) {
        this.createRadarIcon();
      }
      
      // 清理已有的雷达目标图层
      this.cleanupRadarTargetLayer();
      
      // 仅在地图完全就绪时创建图层
      if (createRadarTargetLayer && this.map._container && this.map._panes && this.map._panes.overlayPane) {
        try {
          // 创建新的雷达目标图层
          this.radarTargetLayer = createRadarTargetLayer(
            this.map, 
            this.radarIcon, 
            this.radarTargets,
            this.showRadarInfo
          );
          
          // 设置图层完成初始化的标志
          if (this.radarTargetLayer) {
            console.log('雷达目标图层创建成功');
            this.radarLayerInitialized = true;
            
            // 确保雷达目标图层在船舶图层下方
            if (this.radarTargetLayer._container) {
              this.radarTargetLayer._container.style.zIndex = 290;
            }
            
            // 立即更新雷达目标标记
            if (this.radarTargets && this.radarTargets.length > 0) {
              // console.log('初始化完成后立即更新雷达目标标记，数量:', this.radarTargets.length);
              this.updateRadarTargetMarkers();
            }
          } else {
            console.warn('雷达目标图层创建失败');
          }
        } catch (e) {
          console.error('创建雷达目标图层时出错:', e);
        }
      } else {
        console.warn('地图未完全就绪或工具函数不可用，暂不创建雷达目标图层');
      }
    },
    
    // 添加清理雷达目标图层的专用方法
    cleanupRadarTargetLayer() {
      if (this.radarTargetLayer) {
        try {
          console.log('清理现有雷达目标图层');
          
          // 移除所有雷达目标标记
          if (this.radarTargetMarkers && this.radarTargetMarkers.length > 0) {
            try {
              this.radarTargetLayer.removeLayers(this.radarTargetMarkers);
            } catch (e) {
              console.warn('移除雷达目标标记失败:', e);
            }
            this.radarTargetMarkers = [];
          }
          
          // 从地图中移除图层
          if (this.map && this.map.hasLayer(this.radarTargetLayer)) {
            this.map.removeLayer(this.radarTargetLayer);
          }
          
          // 重置图层引用和状态
          this.radarTargetLayer = null;
          this.radarLayerInitialized = false;
        } catch (e) {
          console.warn('清理雷达目标图层时出错:', e);
        }
      }
    },
    
    toggleRadarTargetVisibility(visible) {
      if (!this.radarTargetLayer || !this.map) {
        console.warn('雷达目标图层未初始化，无法切换可见性');
        return;
      }
      
      console.log(`切换雷达目标图标可见性: ${visible ? '显示' : '隐藏'}, 标记数量:`, this.radarTargetMarkers.length);
      
      try {
        if (visible) {
          // 显示雷达目标图标
          if (this.radarTargetMarkers.length === 0) {
            // 如果没有标记，重新创建
            this.updateRadarTargetMarkers();
          } else {
            // 如果已有标记，添加到图层
            this.radarTargetLayer.addLayers(this.radarTargetMarkers);
          }
          // 强制重绘Canvas图层
          this.radarTargetLayer._redraw(true);
        } else {
          // 隐藏雷达目标图标
          if (this.radarTargetMarkers.length > 0) {
            this.radarTargetLayer.removeLayers(this.radarTargetMarkers);
            // 强制重绘Canvas图层，清除已删除的标记
            this.radarTargetLayer._redraw(true);
          }
        }
      } catch (error) {
        console.error('切换雷达目标可见性失败:', error);
      }
    },
    
    updateRadarTargetMarkers() {
      // 如果Canvas图层未初始化，跳过更新
      if (!this.radarTargetLayer || !this.radarLayerInitialized) {
        console.warn('雷达目标图层未初始化，跳过更新雷达目标标记');
        return;
      }
      
      try {
        // 确保雷达目标数据和图标存在
        if (!this.radarTargets || !Array.isArray(this.radarTargets) || this.radarTargets.length === 0 || !this.radarIcon) {
          console.warn('雷达目标数据或图标不可用，跳过更新雷达目标标记');
          return;
        }
        
        // 确保Canvas图层有必要的方法
        if (!this.radarTargetLayer.addLayers || !this.radarTargetLayer.removeLayers) {
          console.warn('雷达目标图层缺少必要方法，跳过更新');
          return;
        }
        
        // 移除现有标记
        if (this.radarTargetMarkers && this.radarTargetMarkers.length > 0) {
          try {
            this.radarTargetLayer.removeLayers(this.radarTargetMarkers);
          } catch (e) {
            console.warn('移除现有雷达目标标记失败:', e);
          }
          this.radarTargetMarkers = [];
        }
        
        // 使用工具函数创建新的雷达目标标记
        if (updateRadarTargetMarkers) {
          this.radarTargetMarkers = updateRadarTargetMarkers(
            this.radarTargetLayer,
            this.radarTargets,
            this.radarIcon
          );
          // console.log('成功创建新雷达目标标记，数量:', this.radarTargetMarkers.length);
        } else {
          console.error('updateRadarTargetMarkers函数不可用');
        }
      } catch (e) {
        console.error('更新雷达目标标记时出错:', e);
      }
    },
    
    showRadarInfo(radarTarget) {
      if (!radarTarget) {
        console.warn('雷达目标数据无效，无法显示信息');
        return;
      }
      
      // 更新当前选中的雷达目标
      this.currentRadarTarget = radarTarget;
      
      // 打开信息对话框
      this.radarDialogVisible = true;
      
      console.log('已显示雷达目标信息对话框，ID:', radarTarget.targetId, '名称:', radarTarget.targetName || '未知');
    },
    
    closeRadarDialog() {
      // 关闭雷达目标信息对话框
      this.radarDialogVisible = false;
      console.log('关闭雷达目标信息对话框');
    },
    
    // 关闭船舶面板
    closeShipPanel() {
      this.shipPanelVisible = false;
      // 清除选中高亮圈与闪烁定时器（问题#5：关闭面板后选中状态残留）
      this.clearHighlight();
      // 统一语义：面板关闭即无选中船舶（手动预警入口会提示重新选船）
      this.currentShip = null;
    },
    
    // 显示船舶告警（手动预警入口）
    showShipWarning() {
      if (!this.currentShip) {
        this.$message.warning('请先在地图上点击目标船舶，再使用手动预警');
        return;
      }
      // 通过显示 ShipInfoPanel 并触发其告警对话框来实现
      this.shipPanelVisible = true;
      this.$nextTick(() => {
        if (this.$refs.shipInfoPanel) {
          this.$refs.shipInfoPanel.onShowWarning();
        }
      });
    },
    
    // 格式化经度为度分秒格式
    formatLongitudeDMS(longitude) {
      if (longitude === undefined || longitude === null) return '-';
      return this.convertToDMS(longitude, 'lng');
    },
    
    // 格式化纬度为度分秒格式
    formatLatitudeDMS(latitude) {
      if (latitude === undefined || latitude === null) return '-';
      return this.convertToDMS(latitude, 'lat');
    },
    
    // 将坐标转换为度分秒格式
    convertToDMS(coordinate, type) {
      return convertToDMS(coordinate, type);
    },
    
    /**
     * 更新ships数组中的船舶信息
     * @param {string} mmsi - 船舶MMSI号
     * @param {object} vesselData - API返回的船舶详细信息
     */
    updateShipInShipsArray(mmsi, vesselData) {
      if (!mmsi || !vesselData) return;
      
      // 查找ships数组中对应的船舶
      const shipIndex = this.ships.findIndex(ship => ship.userid === mmsi);
      
      if (shipIndex !== -1) {
        // 找到了对应的船舶，更新其信息
        const existingShip = this.ships[shipIndex];
        
        // 合并更新船舶信息，保留AIS实时数据
        this.ships.splice(shipIndex, 1, {
          ...existingShip,
          shipname: sanitizeVesselName(vesselData.vesselName || vesselData.cnName) || existingShip.shipname,
          cnName: sanitizeVesselName(vesselData.cnName || vesselData.vesselName) || existingShip.cnName,
          // 保留位置和AIS实时数据
          pos: existingShip.pos,
          cog: existingShip.cog,
          sog: existingShip.sog,
          heading: existingShip.heading,
          receive_time: existingShip.receive_time
        });
        
        console.log(`已更新ships数组中的船舶信息: ${vesselData.cnName || vesselData.vesselName} (${mmsi})`);
        
        // 更新船舶标记以反映新的船舶名称
        this.$nextTick(() => {
          this.updateShipMarkers();
        });
      } else {
        console.log(`ships数组中未找到MMSI为${mmsi}的船舶，可能是新船舶或尚未接收到AIS数据`);
      }
    },
    
    // 获取船舶详细信息
    async fetchVesselDetailByMmsi(mmsi) {
      try {
        const response = await getVesselByMmsi(mmsi);
        if (response && response.code === 200 && response.data) {
          const vesselData = response.data;
          
          // 更新当前船舶数据
          this.currentShip = {
            ...this.currentShip,
            userid: vesselData.mmsi, // 使用API返回的MMSI更新userid
            shipname: sanitizeVesselName(vesselData.vesselName || vesselData.cnName) || this.currentShip.shipname,
            cnName: sanitizeVesselName(vesselData.cnName) || this.currentShip.cnName,
            shipType: vesselData.vesselType || this.currentShip.shipType,
            callSign: vesselData.callSign,
            flag: vesselData.flag,
            length: vesselData.length,
            width: vesselData.width,
            depth: vesselData.depth,
            load: vesselData.load,
            grossTonnage: vesselData.grossTonnage,
            buildYear: vesselData.buildYear,
            imo: vesselData.imo,
            contacts: vesselData.contacts,
            contactNumber: vesselData.contactNumber,
            remark: vesselData.remark,
            // 保留位置和AIS数据
            pos: this.currentShip.pos,
            cog: this.currentShip.cog,
            sog: this.currentShip.sog,
            heading: this.currentShip.heading,
            receive_time: this.currentShip.receive_time
          };
          
          console.log('已从API获取船舶详细信息:', vesselData);
          
          // 🔥 关键优化：更新ships数组中的船舶信息，以便地图上正确显示中文名称
          this.updateShipInShipsArray(mmsi, vesselData);
        }
      } catch (error) {
        console.error('获取船舶详情失败:', error);
        // 不影响UI显示，只在控制台打印错误信息
      }
    },
    
    // 加载船舶类型
    async loadVesselTypes() {
      if (this.vesselTypeLoaded) return;
      
      try {
        const response = await getAllVesselTypes();
        if (response && response.code === 200 && response.data) {
          this.vesselTypeOptions = response.data.map(type => ({
            value: type.code || type.name,
            label: type.name
          }));
          
          // 如果API没有返回数据，使用默认选项
          if (this.vesselTypeOptions.length === 0) {
            this.useDefaultVesselTypes();
          }
          
          this.vesselTypeLoaded = true;
          console.log('已加载船舶类型数据:', this.vesselTypeOptions);
        } else {
          this.useDefaultVesselTypes();
        }
      } catch (error) {
        console.error('获取船舶类型失败:', error);
        this.useDefaultVesselTypes();
      }
    },
    
    // 使用默认船舶类型
    useDefaultVesselTypes() {
      this.vesselTypeOptions = [
        { value: '货船', label: '货船' },
        { value: '客船', label: '客船' },
        { value: '油轮', label: '油轮' },
        { value: '渔船', label: '渔船' },
        { value: '集装箱船', label: '集装箱船' },
        { value: '散货船', label: '散货船' },
        { value: '特种船', label: '特种船' },
        { value: '其他', label: '其他' }
      ];
      this.vesselTypeLoaded = true;
      console.log('使用默认船舶类型数据');
    },
    

    
    // 处理来自ShipTrackViewer的定位事件
    onTrackLocateShip(data) {
      console.log('收到航迹组件的定位事件:', data);
      
      if (data && data.position) {
        // 清除之前的高亮效果
        this.clearHighlight();
        
        // 设置地图视图中心点
        if (this.map) {
          this.map.setView([data.position.lat, data.position.lng], 16);
          
          // 创建一个临时标记，使用与highlightShip相同的视觉效果
          const position = [data.position.lat, data.position.lng];
          try {
            // 创建一个临时标记
            this.highlightMarker = L.circleMarker(position, {
              radius: 25,  // 增大半径
              color: '#0078ff', // 使用蓝色，区别于普通船舶高亮的红色
              weight: 3,   // 增加边框宽度
              opacity: 0.9,
              fillColor: '#00a8ff',
              fillOpacity: 0.4,
              className: 'ship-track-highlight-marker',
              // 确保高亮标记在船舶图层之上
              pane: 'markerPane', // 使用Leaflet的markerPane，它通常在其他图层之上
              zIndexOffset: 1000   // 使用zIndexOffset而不是setZIndex
            }).addTo(this.map);
            
            // 添加闪烁效果
            let opacity = 0.9;
            let increasing = false;
            this.highlightInterval = setInterval(() => {
              if (increasing) {
                opacity += 0.1;
                if (opacity >= 0.9) {
                  opacity = 0.9;
                  increasing = false;
                }
              } else {
                opacity -= 0.1;
                if (opacity <= 0.4) {
                  opacity = 0.4;
                  increasing = true;
                }
              }
              
              if (this.highlightMarker) {
                this.highlightMarker.setStyle({ opacity: opacity });
              } else {
                if (this.highlightInterval) {
                  clearInterval(this.highlightInterval);
                  this.highlightInterval = null;
                }
              }
            }, 100);
            
          } catch (error) {
            console.error('高亮显示航迹点时出错:', error);
          }
        }
        
        // 显示成功消息
        const shipName = data.shipName || data.mmsi || '未知船名';
        this.$message.success(`已定位到船舶 ${shipName} 的航迹点`);
      }
    },
    // 处理ShipTrackViewer组件销毁前的事件
    onTrackViewerBeforeDestroy() {
      console.log('接收到ShipTrackViewer组件销毁前的通知，执行清理工作');
      
      // 清除之前的高亮效果，避免多个高亮标记同时存在
      this.clearHighlight();
    },
  }
}; 
